import { useState, useEffect, useRef, useMemo } from "react";
import { Button } from "../../Components/UI/button";
import { Input } from "../../Components/UI/input";
import { Badge } from "../../Components/UI/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../Components/UI/table";
import {
  TooltipProvider,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../Components/UI/tooltip";
import {
  Plus,
  Search,
  Filter,
  ArrowUp,
  ArrowDown,
  Loader2,
  UserIcon,
  CalendarIcon,
  ArrowDownWideNarrow,
  ClockFadingIcon,
  ChevronsUpDown,
  Check,
  UserCircle,
  Pencil,
  HeadphonesIcon,
  SquareArrowOutUpRight,
  MessageSquareText,
  Paperclip
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Card, CardContent } from "../../Components/UI/card";
import {
  checkIfInqOnlyMode,
  getActiveThreadTypesArr,
  getThreadTypeByTID,
  getThreadTypesArr,
  getUsersArr,
  isAgencyAllowed,
  isSmartSellerMode,
  selectDisqlReasons,
  selectRejectionReasons,
} from "../../Store/Reducers";
import { getTableDateOrWeekday } from "../../Utils/DateTime";
import PaginationComp from "../../Components/Common/Pagination";
import ThreadEntry from "./ThreadEntry";
import {
  formatAmount,
  getColorClass,
  getDateRangeOptions,
  getLastDateColor,
  getOtherMonthOptions,
  getOtherQuarterOptions,
  getOtherYearOptions,
  getPastMonthOptions,
  getPastQuarterOptions,
  getTaskColor,
  getThreadTypeMeta,
  isRecordInDateRange,
  timeAgo,
  toAllWordsLowerCase,
  months,
  currentUserHasPermission,
  FormatAmount,
  getThreadNameFromTypeID,
  getCurrentUser
} from "../../Utils/Utilities";
import ContactActions from "../../Components/Common/ContactActions";
import { CommonFilter } from "../../Components/Common/CommonFilter";
import PeriodFilter from "../../Components/Common/PeriodFilter";
import { cn } from "../../Lib/Cn";
import { format } from "date-fns";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
} from "../../Components/UI/drawer";
import DrawerFilter from "../../Components/Common/DrawerFilter";
import { DatePicker } from "../../Components/Common/DatePicker";
import { RiResetLeftFill } from "react-icons/ri";
import Integrations from "./Integrations";
import useIsMobile from "../../Hooks/useIsMobile";
import { BsViewStacked, BsWhatsapp } from "react-icons/bs";
import { MdPhone } from "react-icons/md";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useCurrency } from "../../Hooks/useCurrency";
import ThreadDropdown from "./AddThreadDropdown";
import ThreadSahiIcon from "../../Components/Common/ThreadSahiIcon";
import { Checkbox } from "../../Components/UI/checkbox";
import { getThreadsAPI, performThreadActionAPI } from "./ThreadsAPI";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../Components/UI/popover";
import { checkAPIResponse } from "../../Utils/APIResponseUtils";
import { setGlobalLoading } from "../../Store/Actions";
import ThrDetails from "./ThrDetails";
import ConnectionDetails from "../Connections/ConnectionDetails";
import ConnectionDetailsMobile from "../Connections/ConnectionDetailsMobile";
import AttachmentPreviewModal from "../../Components/Common/AttachmentPreviewModal";
import { getAgencyData, getConfigData, getMasterDataFromConfig } from "../../Utils/Storage";
import { getThreadModeTitle } from "../../Utils/Utilities";
import { getThreadByID } from "../../Utils/IndexedDBUtils";
import { History, ShoppingCart, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import ConditionalDiv from "../../Components/Common/ConditionalDiv";
import { DatePickerWithRange } from "../../Components/Common/DatePickerWithRange";

const receivedOnFilter = [
  { term: "Today", code: 0 },
  { term: "Yesterday", code: 1 },
  { term: "Last 7 days", code: 2 },
  { term: "Last month", code: 3 },
];

const dueOnFilter = [
  { term: "Overdue", code: 0 },
  { term: "Yesterday", code: 1 },
  { term: "Today", code: 2 },
  { term: "Tomorrow", code: 3 },
  { term: "This week", code: 4 },
  { term: "Next 7 days", code: 5 },
];

const nextTaskFilter = [
  { term: "Not set", code: 0 },
  { term: "Overdue", code: 1 },
  { term: "Yesterday", code: 2 },
  { term: "Today", code: 3 },
  { term: "Next 7 days", code: 4 },
  { term: "Next 30 days", code: 5 },
];

const sortKeyMap = {
  bizName: "connectionData.bizName", // Match this to your sortData logic just for name not value.
  status: "currStageID",
};

const sidebarIcons = [
  { key: "connID", icon: UserCircle },
  { key: "stage", icon: BsViewStacked },
  { key: "assignedTo", icon: UserIcon },
  { key: "dateRange", icon: ClockFadingIcon },
  { key: "sort", icon: ArrowDownWideNarrow },
];

const sortKeyMapMobileFilter = {
  // "Due (oldest first)": { key: "dueDT", direction: "asc", },
  "Received (newest first)": { key: "sinceDT", direction: "desc" },
  // "Next action (oldest first)": { key: "nextTaskDT", direction: "asc", },
  "Received (oldest first)": { key: "sinceDT", direction: "asc" },
  "Amount (highest first)": { key: "bizProspect", direction: "desc" },
};

//Local CSS Classes
let checkBoxCss =
  "data-[state=checked]:bg-sahi-primary-color data-[state=checked]:border-sahi-primary-color data-[state=checked]:text-white  transition-colors";
var filterBtn =
  "button-config sahi-primary-button flex items-center justify-center group px-2.5 py-[5px]";

const getCurrentMonthYear = () => format(new Date(), "MMMM yyyy");
const getTodayDateStr = () => format(new Date(), "yyyy-MM-dd");
const getCurrentQuarterYear = () => {
  const now = new Date();
  const q = Math.ceil((now.getMonth() + 1) / 3);
  return `Q${q} ${now.getFullYear()}`;
};

const Threads = ({ initialTID = null, embedded = false, activeTab: parentActiveTab = "", searchTerm: externalSearchTerm = "", onFilterStateChange, onCountChange }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const clickedTabRef = useRef(null);
  const parentRef = useRef(null);
  const monthParamProcessedRef = useRef(false); // Track if monthParam has been processed
  const inFlightRef = useRef(null);
  const dispatch = useDispatch();
  //Query Params
  const searchParams = new URLSearchParams(location.search);
  const parsedTID = parseInt(searchParams.get("tID") || searchParams.get("tid"), 10);
  const tID = !isNaN(parsedTID) ? parsedTID : (initialTID !== null ? initialTID : 2);
  const monthParam = searchParams.get("m");
  const prdParam = searchParams.get("prd"); // Y for Yesterday, T for Today

  useEffect(() => {
    if (embedded && typeof externalSearchTerm === "string") {
      setSearchTerm(externalSearchTerm);
    }
  }, [embedded, externalSearchTerm]);

  useEffect(() => {
    if (!embedded) return;
    const handleToggle = () => {
      setIsFilterSelected(true);
      setIsDrawerOpen(true);
    };
    window.addEventListener("sales-toggle-filter", handleToggle);
    return () => {
      window.removeEventListener("sales-toggle-filter", handleToggle);
    };
  }, [embedded]);
  const thrActions = currentUserHasPermission("ThrActions");
  const canCreateThread = thrActions >= 1 && thrActions <= 3;
  const threadModeType = searchParams.get("mode");
  const statusIDParam = searchParams.get("sID");
  const reasonIDParam = searchParams.get("rID");
  const reasonTypeParam = searchParams.get("rTyp");
  const [paramConnID, setParamConnID] = useState("");
  const [paramStage, setParamStage] = useState("");
  const [paramStatus, setParamStatus] = useState("");
  const [paramReasonID, setParamReasonID] = useState("");
  const [paramReasonType, setParamReasonType] = useState("");
  const [paramDateRange, setParamDateRange] = useState("");
  const [paramOtherMonth, setParamOtherMonth] = useState("");
  const [paramOtherQuarter, setParamOtherQuarter] = useState("");
  const [paramOtherYear, setParamOtherYear] = useState("");
  const [paramExecutiveID, setParamExecutiveID] = useState("");
  const [paramProductID, setParamProductID] = useState("");
  const isAgencyAuthorized = useSelector(isAgencyAllowed);
  const thrViewTypeID = currentUserHasPermission("ThrViewTypeID");
  const isAssignedOnly = thrViewTypeID === 2;

  const {
    data: threadData,
    loading,
    error,
  } = useSelector((state) => state.threadData);
  const { data: connectionData } = useSelector((state) => state.connectionData);
  const segments = useSelector((state) => state.masterData?.segments) || [];
  const segmentsWithColors = useMemo(() => {
    return segments?.map((seg) => ({ CNm: seg.CNm, Clr: `#${seg.Clr}` })) || [];
  }, [segments]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(true);
  const isGlobalLoading = useSelector((state) => state.isGlobalLoading);
  const users = useSelector(getUsersArr);
  const activeData = useSelector((state) => state.activeData);

  const monthlyJson = useMemo(() => {
    const raw = activeData.data.MonthlyThreads;
    if (typeof raw === 'string') return JSON.parse(raw);
    if (Array.isArray(raw)) return raw;
    return [];
  }, [activeData.data.MonthlyThreads]);

  const availablePeriods = useMemo(() => {
    const monthsSet = new Set();
    const quartersSet = new Set();
    const yearsSet = new Set();

    const now = new Date();
    const currentMonthName = months[now.getMonth()];
    const currentYear = now.getFullYear();

    monthsSet.add(`${currentMonthName} ${currentYear}`);
    quartersSet.add(`Q${Math.ceil((now.getMonth() + 1) / 3)} ${currentYear}`);
    yearsSet.add(currentYear.toString());

    if (Array.isArray(monthlyJson)) {
      monthlyJson.forEach(item => {
        if (item.YY && item.MM) {
          const y = item.YY.toString();
          const mName = months[item.MM - 1];
          const q = Math.ceil(item.MM / 3);

          if (mName) monthsSet.add(`${mName} ${y}`);
          quartersSet.add(`Q${q} ${y}`);
          yearsSet.add(y);
        }
      });
    }

    const sortedMonths = Array.from(monthsSet).sort((a, b) => {
      return new Date(b) - new Date(a);
    });

    return {
      months: sortedMonths,
      quarters: Array.from(quartersSet).sort().reverse(),
      years: Array.from(yearsSet).sort().reverse(),
    };
  }, [monthlyJson]);
  const threadTypes = useSelector(getActiveThreadTypesArr);
  const threadTypesAll = useSelector((state) => state.masterData.threadTypes);
  const threadType = useSelector(getThreadTypeByTID(tID));
  const products = useSelector((state) => state.masterData?.products || []);
  const [threadTypeInfo, setThreadTypeInfo] = useState({}); // Default to Inquiry initially
  const [threads, setThreads] = useState([]);
  const dropdownProducts = useMemo(() => {
    return getMasterDataFromConfig('Products') || [];
  }, []);
  const [threadTypeCnt, setThreadTypeCnt] = useState({});
  const [filteredThreads, setFilteredThreads] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [threadEntryOpen, setThreadEntryOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("sort");
  const [open, setOpen] = useState(false);
  const [preSearchThreads, setPreSearchThreads] = useState([]); // filtered by filters only
  const [selectedThread, setSelectedThread] = useState({});
  const [selectedThreadDetail, setSelectedThreadDetail] = useState({});
  const [isThreadDetialsOpen, setIsThreadDetailsOpen] = useState(false);
  const [isConnDetailOpen, setIsConnDetailOpen] = useState(false);
  const [isMobileConnDetailOpen, setIsMobileConnDetailOpen] = useState(false);
  const [previewFilePath, setPreviewFilePath] = useState(null);
  const [selectedConnID, setSelectedConnID] = useState(0);
  const [expandedThreadID, setExpandedThreadID] = useState(null);
  const [isDecimal, setIsDecimal] = useState(false);
  const totalThreadCnt = Object.values(threadTypeCnt).reduce(
    (acc, val) => acc + val,
    0
  );
  const prevFiltersRef = useRef({});
  const [selectedThreadTypeFromDropdown, setSelectedThreadTypeFromDropdown] =
    useState(tID && Number(tID) !== 0 ? Number(tID) : null);
  const [historyMode, setHistoryMode] = useState(null);
  const [hideTypeColumn, setHideTypeColumn] = useState(false);
  const currencySymbol = useCurrency();
  const [connData, setConndata] = useState([]);
  const [sourceThreads, setSourceThreads] = useState([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const handleFdbkRequest = async (targetItem = null) => {
    if (!targetItem) return;
    dispatch(setGlobalLoading(true));
    try {
      const threadData = {
        ...targetItem,
        threadID: targetItem.ThreadID || targetItem.threadID || targetItem.actionID,
        typeID: targetItem.TypeID || targetItem.typeID || 2,
        pocUserID: targetItem.POCUserID || targetItem.pocUserID || 0,
        shareByWhatsApp: true,
        shareByEmail: false,
        takeFeedback: true,
        connID: targetItem.ConnID || targetItem.connID,
        reference: targetItem.Reference || targetItem.reference,
        product: targetItem.MainProduct || targetItem.mainProduct || targetItem.Product || targetItem.product,
        connectionData: {
          bizName: targetItem.BizName || targetItem.bizName || targetItem.connectionData?.bizName || targetItem.connectionData?.BizName,
          contactName: targetItem.ContactName || targetItem.contactName || targetItem.ContactPerson || targetItem.connectionData?.contactName || targetItem.connectionData?.ContactName,
          email: targetItem.Email || targetItem.email || targetItem.connectionData?.email || targetItem.connectionData?.Email,
          mobile: targetItem.Mobile || targetItem.mobile || targetItem.connectionData?.mobile || targetItem.connectionData?.Mobile,
          cc: targetItem.CC || targetItem.cc || targetItem.connectionData?.cc || targetItem.connectionData?.CC
        }
      };

      const result = await performThreadActionAPI(threadData, 'FDBKREQ');
      if (result.error === false) {
        toast.success("Feedback request sent on WhatsApp.");
      } else {
        toast.error(result.message || "Failed to send feedback request.");
      }
    } catch (err) {
      console.error('Error while sending feedback request', err);
      toast.error("Failed to send feedback request.");
    } finally {
      dispatch(setGlobalLoading(false));
    }
  };

  const handleThreadTypeSelect = (threadTypeID) => {
    const typeID = threadTypeID || (tID && Number(tID) !== 0 ? Number(tID) : 1);
    setSelectedThreadTypeFromDropdown(typeID);
    setSelectedThread(null);
    setThreadEntryOpen(true);
  };
  const [modeValue, setModeValue] = useState("Processed"); // default
  const options = ["Open", "Processed"];
  const [isTypePopoverOpen, setIsTypePopoverOpen] = useState(false);
  const isInqOnlyMode = useSelector(checkIfInqOnlyMode);
  const disqlReasons = useSelector(selectDisqlReasons);
  const rejectionReasons = useSelector(selectRejectionReasons);


  const getMonthFilterFromNumber = (monthNumber) => {
    if (!monthNumber)
      return {
        dateRange: "all",
        otherMonth: "all",
        otherQuarter: "all",
        otherYear: "all",
      };

    const currentDate = new Date();
    const currentMonth = currentDate.getMonth() + 1;
    const currentYear = currentDate.getFullYear();
    const targetMonth = parseInt(monthNumber, 10);

    if (targetMonth === currentMonth) {
      return {
        dateRange: "This Month",
        otherMonth: "all",
        otherQuarter: "all",
        otherYear: "all",
      };
    } else {
      let targetYear = currentYear;
      if (currentMonth === 1) {
        targetYear = currentYear - 1;
      } else {
        targetYear = targetMonth > currentMonth ? currentYear - 1 : currentYear;
      }
      const monthName = months[targetMonth - 1];
      return {
        dateRange: "Other Month",
        otherMonth: `${monthName} ${targetYear}`,
        otherQuarter: "all",
        otherYear: "all",
      };
    }
  };

  const getDateFromPrdParam = (prd) => {
    if (!prd) return null;
    const today = new Date();
    let targetDate;

    if (prd === 'T') {
      // Today
      targetDate = today;
    } else if (prd === 'Y') {
      // Yesterday
      targetDate = new Date(today);
      targetDate.setDate(targetDate.getDate() - 1);
    } else {
      return null;
    }

    const year = targetDate.getFullYear();
    const month = String(targetDate.getMonth() + 1).padStart(2, '0');
    const day = String(targetDate.getDate()).padStart(2, '0');
    const dateString = `${year}-${month}-${day}`;
    return dateString;
  };

  const oldestSinceDT = useMemo(() => {
    const dates = [];

    if (connectionData && connectionData.length > 0) {
      connectionData.forEach((conn) => {
        const dateStr = conn.SinceDT || conn.CreatedDT;
        if (dateStr) {
          const d = new Date(dateStr);
          if (!isNaN(d.getTime())) dates.push(d);
        }
      });
    }

    if (threads && threads.length > 0) {
      threads.forEach((thr) => {
        const dateStr = thr.sinceDT || thr.createdDT || thr.dueDT || thr.SinceDT || thr.CreatedDT;
        if (dateStr) {
          const d = new Date(dateStr);
          if (!isNaN(d.getTime())) dates.push(d);
        }
      });
    }

    if (monthlyJson && Array.isArray(monthlyJson)) {
      monthlyJson.forEach((item) => {
        if (item.YY && item.MM) {
          const d = new Date(Number(item.YY), Number(item.MM) - 1, 1);
          if (!isNaN(d.getTime())) dates.push(d);
        }
      });
    }

    if (dates.length === 0) return new Date();

    return new Date(Math.min(...dates));
  }, [connectionData, threads, monthlyJson]);
  const getPeriodFiltersFromParams = () => {
    const dateRangeParam =
      searchParams.get("p") || searchParams.get("dateRange");
    const otherMonthParam =
      searchParams.get("om") || searchParams.get("otherMonth");
    const otherQuarterParam =
      searchParams.get("oq") || searchParams.get("otherQuarter");
    const otherYearParam =
      searchParams.get("oy") || searchParams.get("otherYear");

    if (dateRangeParam) {
      return {
        dateRange: dateRangeParam,
        otherMonth: otherMonthParam || "all",
        otherQuarter: otherQuarterParam || "all",
        otherYear: otherYearParam || "all",
      };
    }
    return null;
  };

  const periodFiltersFromParams = getPeriodFiltersFromParams();
  const initialMonthFilters = useMemo(() => {
    // 1. Priority: Direct period params (?p=...&om=...)
    if (periodFiltersFromParams) return periodFiltersFromParams;

    // 2. Secondary: Month param (?m=12)
    if (monthParam) return getMonthFilterFromNumber(monthParam);

    // 3. Default
    return {
      dateRange: "all",
      otherMonth: "all",
      otherQuarter: "all",
      otherYear: "all",
    };
  }, [periodFiltersFromParams, monthParam]);

  const [sortConfig, setSortConfig] = useState({
    key: "sinceDT",
    direction: "desc",
  });
  const [initialSortApplied, setInitialSortApplied] = useState(false);
  const [isFilterSelected, setIsFilterSelected] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({
    stage: "all",
    assignedTo: "all",
    receivedOn: "all",
    dueOn: "all",
    nextTaskOn: "all",
    types: tID && Number(tID) !== 0 ? String(tID) : "1",
    connID: "all",
    dateRange: initialMonthFilters.dateRange === "all" ? "This Month" : initialMonthFilters.dateRange, otherMonth: initialMonthFilters.otherMonth,
    otherQuarter: initialMonthFilters.otherQuarter,
    otherYear: initialMonthFilters.otherYear,
    status: "all",
    reason: "all",
    reasonID: "all",
    reasonType: "all",
    selectDate: false,
    selectedDate: null,
    productID: "all",
  });
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [tempFilters, setTempFilters] = useState(selectedFilters);
  const [tempsortConfig, setTempsortConfig] = useState(sortConfig);
  const [stageInfo, setStageInfo] = useState([]);

  //functions to get date range filters options
  //Threads table header Obj
  const threadTableHeadersData = [
    {
      key: "type",
      label: "",
      className: "align-middle text-text-color w-[3vw] ",
    },
    { key: "reference", label: "Reference", className: "align-middle" },
    {
      key: "bizName",
      label: "Contact",
      className: "align-middle text-text-color",
    },
    {
      key: "mainProduct",
      label: "Offering",
      className: "lg:w-[50px] xl:w-[50px] 2xl:w-[150px] align-middle",
    },
    {
      key: "bizProspect",
      label: (
        <label className="text-sm font-medium text-gray-900 whitespace-nowrap">
          Amount (
          <span className="whitespace-nowrap  text-base">
            {currencySymbol}
          </span>
          )
        </label>
      ),
      className: "flex justify-end items-start text-right align-middle",
    },
    { key: "sinceDT", label: "Received on", className: "align-middle" },
    { key: "dueDT", label: "Due on", className: "align-middle" },
    { key: "status", label: "Status", className: "align-middle" },
    {
      key: "nextTaskDT",
      label: "Next Action",
      className: "align-middle text-start break-words whitespace-normal",
    },
    { key: "pocUserName", label: "Executive", className: "align-middle" },
  ];

  const convertedTIDs = tempFilters.types
    ? tempFilters.types
      .toString()
      .split("")
      .map((tID) => parseInt(tID, 10))
    : [];

  const filterOptions = [
    {
      key: "types",
      label: "All Thread Types",
      isHidden: Number(tID) !== 0,
      options: threadTypes.map((t) => ({ name: t.Typ, ID: t.TID.toString() })),
      type: "options",
      mobileDropdownText: "Select Thread Type",
      MobileLabel: "Thread Type",
      showAllOption: false,
      showAllOptionNum: false,
    },

    // {
    //      key: "stage",
    //      label: "All Stages",
    //      options:
    //          stageInfo.find(type => type.TID === convertedTIDs[0])?.Stages.map(stage => ({
    //              name: stage.S,
    //              ID: stage.SID
    //          })) || [],
    //      displayValue: (val) => {
    //          return stageInfo
    //              .find(type => type.TID === convertedTIDs[0])
    //              ?.Stages.find(stage => stage.SID === val)?.S || val
    //      },
    //      mobiledisabled: tempFilters.types.length !== 1,
    //      type: "options",
    //      searchEnabled: false,
    //      disabled: true,
    //      MobileLabel: "Stage",
    //      showAllOption: true
    // },
    {
      key: "dateRange",
      label:
        selectedFilters.dateRange === "all"
          ? "This Month"
          : selectedFilters.dateRange,
      options: getDateRangeOptions().map(option => ({
        name: option,
        ID: option,
      })),
      searchEnabled: false,
      type: "list",
      MobileLabel: "Period",
      mobileLabel: "Period",
      searchEnabled: true,
      displayValue: (val) => {
        if (val === "all") return "This Month";
        return val;
      },
      showAllOption: false,
    },

    // {
    //   key: "otherMonth",
    //   label: "Select Month",
    //   options: (() => {
    //     const generated = getPastMonthOptions(oldestSinceDT);
    //     const current = selectedFilters.otherMonth;
    //     // If current selection is not in the list (out of range), add it to the top
    //     if (current !== "all" && !generated.includes(current)) {
    //       generated.unshift(current);
    //     }
    //     return generated.map(opt => ({ name: opt, ID: opt }));
    //   })(),
    //   disabled: !(
    //     selectedFilters.dateRange === "Other Month" ||
    //     tempFilters.dateRange === "Other Month"
    //   ),
    //   mobiledisabled: !(tempFilters.dateRange === "Other Month"),
    //   type: "list",
    //   mobileDropdownText: "Select Month",
    //   mobileNoLabel: true,
    //   isSorting: false,
    // },
    // {
    //   key: "otherQuarter",
    //   label: "Select Quarter",
    //   options: (() => {
    //     const generated = getPastQuarterOptions(oldestSinceDT);
    //     const current = selectedFilters.otherQuarter;
    //     if (current !== "all" && !generated.includes(current)) {
    //       generated.unshift(current);
    //     }
    //     return generated.map(opt => ({ name: opt, ID: opt }));
    //   })(),
    //   disabled: !(
    //     selectedFilters.dateRange === "Other Quarter" ||
    //     tempFilters.dateRange === "Other Quarter"
    //   ),
    //   mobiledisabled: !(tempFilters.dateRange === "Other Quarter"),
    //   type: "list",
    //   mobileDropdownText: "Select Quarter",
    //   mobileNoLabel: true,
    //   isSorting: false,
    // },
    // {
    //   key: "otherYear",
    //   label: "Select Year",
    //   options: (() => {
    //     const generated = getOtherYearOptions(oldestSinceDT);
    //     const current = selectedFilters.otherYear;
    //     if (current !== "all" && !generated.includes(current)) {
    //       generated.unshift(current);
    //     }
    //     return generated.map(opt => ({ name: opt, ID: opt }));
    //   })(),
    //   disabled: !(
    //     selectedFilters.dateRange === "Other Year" ||
    //     tempFilters.dateRange === "Other Year"
    //   ),
    //   mobiledisabled: !(tempFilters.dateRange === "Other Year"),
    //   type: "list",
    //   mobileDropdownText: "Select Year",
    //   mobileNoLabel: true,
    //   isSorting: false,
    // },

    {
      key: "otherMonth",
      label: "Select Month",
      options: (() => {
        const generated = getPastMonthOptions(oldestSinceDT);
        const current = selectedFilters.otherMonth;
        // If current selection is not in the list, add it to the top
        if (current !== "all" && !generated.some(opt => opt.ID === current)) {
          generated.unshift({ name: current, ID: current });
        }
        return generated; // ← Don't map again!
      })(),
      disabled: !(
        selectedFilters.dateRange === "Other Month" ||
        tempFilters.dateRange === "Other Month"
      ),
      mobiledisabled: !(tempFilters.dateRange === "Other Month"),
      type: "list",
      mobileDropdownText: "Select Month",
      mobileNoLabel: true,
      isSorting: false,
    },
    {
      key: "otherQuarter",
      label: "Select Quarter",
      options: (() => {
        const generated = getPastQuarterOptions(oldestSinceDT);
        const current = selectedFilters.otherQuarter;
        if (current !== "all" && !generated.some(opt => opt.ID === current)) {
          generated.unshift({ name: current, ID: current });
        }
        return generated; // ← Don't map again!
      })(),
      disabled: !(
        selectedFilters.dateRange === "Other Quarter" ||
        tempFilters.dateRange === "Other Quarter"
      ),
      mobiledisabled: !(tempFilters.dateRange === "Other Quarter"),
      type: "list",
      mobileDropdownText: "Select Quarter",
      mobileNoLabel: true,
      isSorting: false,
    },
    {
      key: "otherYear",
      label: "Select Year",
      options: (() => {
        const generated = getOtherYearOptions(oldestSinceDT);
        const current = selectedFilters.otherYear;
        if (current !== "all" && !generated.some(opt => opt.ID === current)) {
          generated.unshift({ name: current, ID: current });
        }
        return generated; // ← Don't map again!
      })(),
      disabled: !(
        selectedFilters.dateRange === "Other Year" ||
        tempFilters.dateRange === "Other Year"
      ),
      mobiledisabled: !(tempFilters.dateRange === "Other Year"),
      type: "list",
      mobileDropdownText: "Select Year",
      mobileNoLabel: true,
      isSorting: false,
    },
    {
      key: "status",
      label: "All Status",
      // FIX: Added 3 to the include list
      isHidden: ![1, 3, 4].includes(Number(tID)),
      options: Number(tID) === 3
        ? [
          { name: "Open", ID: "0" },
          { name: "Closed", ID: "2" }
        ]
        : [
          { name: "Open", ID: "0" },
          { name: "Closed", ID: "2" },
          { name: "Rejected", ID: "3" },
          { name: "Disqualified", ID: "4" }
        ],
      displayValue: (val) => {
        if (Number(tID) === 3) {
          return val === "0" ? "Open" : val === "2" ? "Closed" : "All Status";
        }
        const map = { "0": "Open", "2": "Closed", "3": "Rejected", "4": "Disqualified" };
        return map[val] || "All Status";
      },
      type: "list",
      MobileLabel: "Status",
      showAllOption: true,
      mobileLabel: "Status",
      showAllOptionNum: false,
    },
    ...(selectedFilters.status === "3" ? [{
      key: "reasonID",
      label: "All Reasons",
      options: (() => {
        const typeData = rejectionReasons.find(
          (reason) => String(reason.TID) === String(selectedFilters.types)
        );

        if (typeData && typeData.Rsns) {
          const parsedReasons = JSON.parse(typeData.Rsns);
          return parsedReasons.map((reason) => ({ name: reason.R, ID: String(reason.RID) }));
        }

        return [];
      })(),
      displayValue: (val) => {
        const typeData = rejectionReasons.find(
          (reason) => String(reason.TID) === String(selectedFilters.types)
        );

        if (typeData && typeData.Rsns) {
          const parsedReasons = JSON.parse(typeData.Rsns);
          const foundReason = parsedReasons.find(
            (reason) => String(reason.RID) === String(val)
          );
          return foundReason ? foundReason.R : "All Reasons";
        }

        return "All Reasons";
      },
      type: "list",
      MobileLabel: "Reason",
      showAllOption: true,
      mobileLabel: "Reason",
    }] : []),
    ...(selectedFilters.status === "4" ? [{
      key: "reasonID",
      label: "All Reasons",
      options: (() => {
        const typeData = disqlReasons.find(
          (reason) => String(reason.TID) === String(selectedFilters.types)
        );

        if (typeData && typeData.Rsns) {
          const parsedReasons = JSON.parse(typeData.Rsns);
          return parsedReasons.map((reason) => ({ name: reason.R, ID: String(reason.RID) }));
        }

        return [];
      })(),
      displayValue: (val) => {
        const typeData = disqlReasons.find(
          (reason) => String(reason.TID) === String(selectedFilters.types)
        );

        if (typeData && typeData.Rsns) {
          const parsedReasons = JSON.parse(typeData.Rsns);
          const foundReason = parsedReasons.find(
            (reason) => String(reason.RID) === String(val)
          );
          return foundReason ? foundReason.R : "All Reasons";
        }

        return "All Reasons";
      },
      type: "list",
      MobileLabel: "Reason",
      showAllOption: true,
      mobileLabel: "Reason",
    }] : []),
    {
      key: "connID",
      label: "All Contacts",
      options: connData
        .map((c) => ({
          name: c.bizName || c.contactName,
          ID: c.connID,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
      displayValue: (val) => {
        let displayText = "All Contacts";
        let bizName = connData.find(
          (c) => Number(c.connID) === Number(val)
        )?.bizName;
        if (bizName == "") {
          displayText =
            connData.find((c) => Number(c.connID) === Number(val))
              ?.contactName || "";
        } else {
          displayText = bizName;
        }
        return displayText;
      },
      type: "list",
      mobileDropdownText: "Select Contact",
      MobileLabel: "Contacts",
      showAllOption: true,
      mobileLabel: "Contact",
    },
    {
      key: "productID",
      label: "All Offerings",
      options: dropdownProducts
        .map((p) => ({
          name: p.name || p.PNm,
          ID: String(p.id || p.PID),
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
      displayValue: (val) => {
        if (val === "all") return "All Offerings";
        const found = dropdownProducts.find(p => String(p.id || p.PID) === String(val));
        return found ? (found.name || found.PNm) : "All Offerings";
      },
      type: "list",
      mobileDropdownText: "Select Offering",
      MobileLabel: "Offerings",
      showAllOption: true,
      mobileLabel: "Offering",
    },
    ...(!isSmartSellerMode() && !isAssignedOnly ? [{
      key: "assignedTo",
      label:
        selectedFilters.assignedTo === "unassigned"
          ? "Not Assigned"
          : selectedFilters.assignedTo === "all"
            ? "All Executives"
            : users.find(
              (u) => String(u.userID) === String(selectedFilters.assignedTo)
            )?.name || "All Executives",
      options: [
        { name: "Not Assigned", ID: "unassigned" },
        ...users
          .map((u) => ({ name: u.name, ID: u.userID }))
          .sort((a, b) => a.name.localeCompare(b.name)),
      ],
      displayValue: (val) => {
        if (val === "unassigned") return "Not Assigned";
        if (val === "all") return "All Executives";
        const foundUser = users.find((u) => String(u.userID) === String(val));
        return foundUser?.name || "All Executives";
      },
      type: "list",
      mobileDropdownText: "Select Executive",
      MobileLabel: "Executive",
      showAllOption: true,
      mobileLabel: "Executive",
    }] : []),
    /*{
            key: "status",
            label: "All Status",
            options: [
                { name: "Open", ID: "0" },
                { name: "Completed", ID: "2" },
                { name: "Cancelled", ID: "3" }
            ],
            displayValue: (val) => {
                if (val === "0") return "Open";
                if (val === "2") return "Completed";
                if (val === "3") return "Cancelled";
                return "All Status";
            },
            type: "list",
            mobileDropdownText: "Select Status",
            MobileLabel: "Status",
            showAllOption: true
        },*/
    // {
    //    key: "receivedOn",
    //    label: "Received on",
    //    options: receivedOnFilter.map(item => item.term),
    //    type: "options",
    //    searchEnabled: false
    // },
    // {
    //    key: "dueOn",
    //    label: "Due on",
    //    options: dueOnFilter.map(item => item.term),
    //    type: "options",
    //    searchEnabled: false
    // },
    // {
    //    key: "nextTaskOn",
    //    label: "Next Action on",
    //    options: nextTaskFilter.map(item => item.term),
    //    type: "options",
    //    searchEnabled: false,
    //    disabled: modeValue === "Processed",
    // },
  ];

  // mobile filters icon click button
  const handleFilterIconClick = (key) => {
    setActiveTab(key);
    clickedTabRef.current = true;
    setTimeout(() => {
      clickedTabRef.current = false;
    }, 500); // prevent scroll override for 500ms
    const el = document.getElementById(key);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };
  const getFilteredHeaders = () => {
    return threadTableHeadersData.filter((header) => {
      if (hideTypeColumn && header.key === "type") return false;
      // Hide type column if specific thread type is selected (not "All Threads" view)
      if (header.key === "type" && Number(tID) !== 0) return false;

      if (modeValue === "Processed" && header.key === "nextTaskDT") return false;

      // Show reference column only for Ords (type ID 2)
      if (header.key === "reference" && Number(tID) !== 2) return false;

      if (header.key === "dueDT" && Number(tID) === 2 && isInqOnlyMode) return false;

      if (header.key === "status") {
        if (Number(tID) === 2 || Number(tID) !== 3 || Number(tID) === 6) return false;
        if (selectedFilters.status === "0" && Number(tID) !== 3) return false;
      }

      if (Number(tID) === 1 && header.key === "bizProspect") return false;

      return true;
    }).map(header => {
      // Rename Due on to Close on for Ord History
      if (header.key === "dueDT" && Number(tID) === 2) {
        return { ...header, label: "Closed on" };
      }

      if (Number(tID) === 1 && header.key === "status") {
        if (selectedFilters.status === "2") {
          return {
            ...header,
            label: `Closed Amount (${currencySymbol})`,
            className: "text-right  pr-0"
          };
        }
        if (selectedFilters.status === "3") return { ...header, label: "Rejection Reason" };
        if (selectedFilters.status === "4") return { ...header, label: "Disqualification Reason" };
      }
      return header;
    });
  };  // Helper to determine the empty state description text
  const getEmptyStateDescription = () => {
    const typeName = threadTypeInfo?.typeName?.toLowerCase() || "";

    if (typeName.includes("order") || typeName.includes("sale")) {
      return "View monthly sale records and patterns for revenue analysis";
    }
    // Default to Inquiry text
    return "Track monthly inquiry records and trends across all sales channels";
  };

  // Locate this useEffect in your code
  // Locate this useEffect in your code
  useEffect(() => {
    const typeName = threadType?.TypSng || "Threads";

    let pageTitle = "";

    if (isInqOnlyMode && Number(tID) === 2) {
      pageTitle = "Sales";
    } else {
      // Standard behavior for other thread types or modes
      pageTitle = `${typeName} History`;
    }

    document.title = pageTitle;

    return () => {
      document.title = "SahiClub";
    };
  }, [threadType?.TypSng, isInqOnlyMode, tID]);

  //Mobile Filter Scrollbar Event Listener
  useEffect(() => {
    const waitAndAttachScroll = () => {
      const container = document.querySelector(".filter-scroll-container");

      if (!container) {
        setTimeout(waitAndAttachScroll, 50);
        return;
      }

      const handleScroll = () => {
        if (clickedTabRef.current) return;

        const scrollTop = container.scrollTop;
        const scrollHeight = container.scrollHeight;
        const clientHeight = container.clientHeight;
        const scrollPercent = (scrollTop / (scrollHeight - clientHeight)) * 100;
        const index = Math.min(
          Math.floor((scrollPercent / 100) * sidebarIcons.length),
          sidebarIcons.length - 1
        );
        const key = sidebarIcons[index]?.key;
        setActiveTab((prev) => (key !== prev ? key : prev));
      };
      container.addEventListener("scroll", handleScroll);
      handleScroll();
      return () => container.removeEventListener("scroll", handleScroll);
    };

    waitAndAttachScroll();
  }, [isFilterSelected]);

  useEffect(() => {
    if (isFilterSelected && isMobile) {
      setIsDrawerOpen(true);
      setTempFilters(selectedFilters);
      // setTempsortConfig(sortConfig);
    }
  }, [isFilterSelected, isMobile, threadTypeInfo.typeID]);

  const getExecutiveName = (userID) => {
    if (!userID) return "-";
    const user = users.find((user) => user.userID === Number(userID));
    return user ? user.name : "-";
  };

  useEffect(() => {
    const configData = getConfigData();
    const threadStages = JSON.parse(configData.ThreadStages) || [];
    setStageInfo(threadStages);
  }, []);

  const getStageInfo = (typeID, stageID) => {
    const thread = stageInfo.find((thread) => thread.TID === typeID);
    if (!thread) return null;
    const stage = thread.Stages.find((stage) => stage.SID === stageID);
    if (!stage) return null;
    return { SID: stage.SID, name: stage.S };
  };

  // Utils for safe parsing
  const safeParseArray = (input, fallback = []) => {
    if (Array.isArray(input)) return input;
    try {
      return JSON.parse(input || "[]");
    } catch {
      return fallback;
    }
  };

  // Update selected thread when threads change
  useEffect(() => {
    if (selectedThread && (selectedThread.threadID || selectedThread.ThreadID)) {
      const currentID = selectedThread.threadID || selectedThread.ThreadID;
      const listToSearch = (filteredThreads && filteredThreads.length > 0) ? filteredThreads : sourceThreads;
      const updatedThread = listToSearch.find(
        (thread) => (thread.threadID || thread.ThreadID) === currentID
      );
      if (updatedThread) {
        // Only update if the data actually changed (deep comparison would be better, but checking a key field)
        if (JSON.stringify(updatedThread) !== JSON.stringify(selectedThread)) {
          setSelectedThread(updatedThread);
        }
      }
    }
  }, [filteredThreads, sourceThreads]);

  const handleRowClick = (thread) => {
    if (thread.statusID === 0) {
      setSelectedConnID(thread.connID);
      setSelectedThread(thread);
      if (isMobile) {
        setIsMobileConnDetailOpen(true);
      } else {
        setIsConnDetailOpen(true);
      }
    } else {
      setSelectedThread(thread);
      setIsThreadDetailsOpen(true);
    }
  };

  //connection data for filter
  useEffect(() => {
    if (!Array.isArray(connectionData)) {
      setConndata([]);
      return;
    }
    const processed = connectionData
      .map((item) => ({
        connID: item.ConnID,
        bizName: item.BizName || "", // Fallback to empty string
        contactName: item.ContactName || "",
      }))
      .sort((a, b) => {
        const nameA = a.bizName || "";
        const nameB = b.bizName || "";
        return nameA.localeCompare(nameB);
      });
    setConndata(processed);
  }, [connectionData]);

  //Threadtype wise counts
  useEffect(() => {
    if (!Array.isArray(sourceThreads)) return;
    const countMap = {};
    for (const item of sourceThreads) {
      const typeID = item.TypeID ?? "Unknown";
      countMap[typeID] = (countMap[typeID] || 0) + 1;
    }
    setThreadTypeCnt(countMap);
  }, [sourceThreads]);

  //routing logic
  useEffect(() => {
    setIsLoading(true);
    if (!threadType && tID && Number(tID) !== 0) {
      setThreadTypeInfo({
        typeID: 0,
        typeName: "Threads",
        typeNameSng: "Thread",
      });
    } else if (threadType) {
      setThreadTypeInfo({
        typeID: tID,
        typeName: threadType.Typ,
        typeNameSng: threadType.TypSng,
      });
    } else {
      setThreadTypeInfo({
        typeID: 0,
        typeName: "Threads",
        typeNameSng: "Thread",
      });
    }
    if (threadModeType === "P") {
      setModeValue("Processed");
    }

    if (
      !isMobile &&
      (paramConnID !== "" ||
        paramStage !== "" ||
        statusIDParam ||
        monthParam ||
        reasonIDParam ||
        prdParam)
    ) {
      setIsFilterSelected(true);
    } else {
      setIsFilterSelected(false);
      if (!prdParam && !monthParam) {
        handleResetFilters();
      }
    }

    setIsLoading(false);
  }, [threadType, location.pathname, isMobile, threadModeType, prdParam]); // REMOVED monthParam from dependencies

  useEffect(() => {
    if (prdParam) {
      const dateStr = getDateFromPrdParam(prdParam);
      if (dateStr) {
        setSelectedFilters((prev) => ({
          ...prev,
          selectDate: true,
          selectedDate: dateStr,
        }));
        setTempFilters((prev) => ({
          ...prev,
          selectDate: true,
          selectedDate: dateStr,
        }));
        if (!isMobile) {
          setIsFilterSelected(true);
        }
      }
    } else {
      setSelectedFilters((prev) => ({
        ...prev,
        selectDate: false,
        selectedDate: null,
      }));
      setTempFilters((prev) => ({
        ...prev,
        selectDate: false,
        selectedDate: null,
      }));
    }
  }, [prdParam, isMobile]);

  useEffect(() => {
    if (monthParam) {
      const monthFilters = getMonthFilterFromNumber(monthParam);
      setSelectedFilters((prev) => {
        return {
          ...prev,
          dateRange: monthFilters.dateRange,
          otherMonth: monthFilters.otherMonth,
          otherQuarter: monthFilters.otherQuarter,
          otherYear: monthFilters.otherYear,
        };
      });
      setTempFilters((prev) => {
        return {
          ...prev,
          dateRange: monthFilters.dateRange,
          otherMonth: monthFilters.otherMonth,
          otherQuarter: monthFilters.otherQuarter,
          otherYear: monthFilters.otherYear,
        };
      });
      monthParamProcessedRef.current = true; // Mark monthParam as processed
      if (!isMobile) {
        setIsFilterSelected(true);
      }
    }
  }, [monthParam, isMobile]);

  const hasActiveFilters = useMemo(() => {
    // Get the default values that SHOULD be there based on the URL (e.g. Month or Page ID)
    const defaultMonthFilters = getMonthFilterFromNumber(monthParam);

    return Object.entries(selectedFilters).some(([key, value]) => {
      // 1. Ignore 'types' if it matches the current Page ID or is the default "All"
      if (key === "types") {
        if (value === "123456") return false;
        if (tID && String(value) === String(tID)) return false;
        return value !== "all";
      }

      // 2. Ignore Date/Month filters if they match the URL's default month view
      if (monthParam) {
        if (key === "dateRange" && value === defaultMonthFilters.dateRange)
          return false;
        if (key === "otherMonth" && value === defaultMonthFilters.otherMonth)
          return false;
        if (
          key === "otherQuarter" &&
          value === defaultMonthFilters.otherQuarter
        )
          return false;
        if (key === "otherYear" && value === defaultMonthFilters.otherYear)
          return false;
      }

      // 3. For selectedDate, check if it's not null
      if (key === "selectedDate") return value !== null;

      // 4. For everything else, if it's not "all", it's a user filter
      return value !== "all";
    });
  }, [selectedFilters, tID, monthParam]); // Function to fetch data from API

  useEffect(() => {
    if (embedded && onFilterStateChange) {
      onFilterStateChange(Boolean(hasActiveFilters));
    }
  }, [embedded, hasActiveFilters, onFilterStateChange]);

  useEffect(() => {
    if (embedded && onCountChange && !isLoading && !isFetching) {
      const hasRawData = sourceThreads && sourceThreads.length > 0;
      if (preSearchThreads.length === 0 && hasRawData) {
        return;
      }
      const totalAmt = (preSearchThreads || []).reduce((sum, item) => sum + (Number(item.bizProspect || item.BizProspect || item.amount || item.Amt || 0) || 0), 0);
      onCountChange(preSearchThreads.length, totalAmt);
    }
  }, [embedded, preSearchThreads, onCountChange, isLoading, isFetching, sourceThreads]);
  const fetchProcessedData = async () => {
    try {
      setIsFetching(true);
      dispatch(setGlobalLoading(true));
      const params = {
        statusID: "0",
        threadTypeID: "0",
        prdType: "MM",
        prdQty: "0",
        year: "0",
        month: "0",
      };
      const data = await getThreadsAPI(params);
      checkAPIResponse(data);
      const archivedThreads = JSON.parse(data.updatePackage);
      const dbThreads = archivedThreads.Table1 || [];
      const connLookup = Object.fromEntries(
        (Array.isArray(connectionData) ? connectionData : []).map((item) => [
          item.ConnID,
          {
            connID: item.ConnID,
            bizName: item.BizName,
            cc: item.CC,
            contactName: item.ContactName,
            email: item.Email,
            email: item.Email,
            mobile: item.Mobile,
            task: item.Tasks,
            Notes: item.Notes // Map Notes from connectionData
          },
        ])
      );

      // Merge connection details into each thread
      const merged = dbThreads.map((thread) => ({
        ...thread,
        ...connLookup[thread.ConnID],
      }));
      return merged;
    } catch (error) {
      console.error("Error fetching processed data:", error);
      return [];
    } finally {
      dispatch(setGlobalLoading(false));
      setIsFetching(false);
    }
  };



  const loadData = async () => {
    try {
      const now = new Date();
      // Default: This Month
      let year = now.getFullYear();
      let month = now.getMonth() + 1;
      let prdType = "MM";
      let prdQty = 1;

      if (selectedFilters.selectDate && selectedFilters.selectedDate) {
        const selectedDate = new Date(selectedFilters.selectedDate);
        year = selectedDate.getFullYear();
        month = selectedDate.getMonth() + 1;
        prdType = "MM";
        prdQty = 1;
      } else {

        const p = selectedFilters.dateRange;

        // Resolve Period Parameters
        if (p === "This Month") {
          prdType = "MM";
          prdQty = 1;
          year = now.getFullYear();
          month = now.getMonth() + 1;
        }
        else if (p === "This Year") {
          // FIX: Added handling for 'This Year'
          prdType = "YY";
          prdQty = 1;
          year = now.getFullYear();
          month = 0; // Set to 0 as month is irrelevant for Year type
        }
        else if (p === "This Quarter") {
          // FIX: Calculate current quarter (1-4) and pass as QQ#
          const currentQuarter = Math.ceil((now.getMonth() + 1) / 3);
          prdType = `QQ${currentQuarter}`;
          prdQty = 1;
          year = now.getFullYear();
          month = now.getMonth() + 1; // Month is 0 for Quarter type
        }
        else if (p === "Other Month" && selectedFilters.otherMonth !== "all") {
          const [mName, yStr] = selectedFilters.otherMonth.split(" ");
          month = months.indexOf(mName) + 1;
          year = parseInt(yStr);
          prdType = "MM";
        } else if (p === "Other Quarter" && selectedFilters.otherQuarter !== "all") {
          const [qStr, yStr] = selectedFilters.otherQuarter.split(" ");
          prdType = `QQ${qStr.replace("Q", "")}`;
          year = parseInt(yStr);
        } else if (p === "Other Year" && selectedFilters.otherYear !== "all") {
          prdType = "YY";
          year = parseInt(selectedFilters.otherYear);
        }
      }
      //   const statusID = selectedFilters.status === "all" ? "0" : selectedFilters.status;
      let statusID;
      if (selectedFilters.status === "3" || selectedFilters.status === "4") {
        // Both REJECTED and DISQUALIFIED stored as StatusID=3 in DB
        statusID = "3";
      } else if (selectedFilters.status === "all") {
        statusID = "0";
      } else {
        statusID = selectedFilters.status;
      }

      const threadTypeID = (tID && tID !== 0) ? tID.toString() : "1";

      // CHECK GUARD: If these exact DB parameters are currently in flight or already fetched, STOP.
      const currentKey = `${prdType}_${year}_${month}_${statusID}_${threadTypeID}`;
      if (inFlightRef.current === currentKey) {
        return;
      }

      const prev = prevFiltersRef.current;
      if (
        prev.prdType === prdType &&
        prev.year === year &&
        prev.month === month &&
        prev.statusID === statusID &&
        prev.threadTypeID === threadTypeID
      ) {
        return;
      }

      inFlightRef.current = currentKey;
      dispatch(setGlobalLoading(true));
      setIsFetching(true);

      const payload = {
        statusID: statusID,
        threadTypeID: threadTypeID,
        prdType: prdType,
        prdQty: prdQty.toString(),
        year: year.toString(),
        month: month.toString(),
      };

      const result = await getThreadsAPI(payload);
      checkAPIResponse(result);

      if (result?.updatePackage) {
        const updtPkg = JSON.parse(result.updatePackage);
        const dbThreads = updtPkg.Table1 || [];

        // Map and Update UI
        const mapped = dbThreads.map(mapThreadItem);
        setSourceThreads(mapped);
        setFilteredThreads(mapped);
        setPreSearchThreads(mapped);

        // UPDATE REF with the resolved API params to prevent future redundant calls
        prevFiltersRef.current = {
          prdType,
          year,
          month,
          statusID,
          threadTypeID,
        };
      }
    } catch (error) {
      console.error("Fetch Error:", error);
    } finally {
      inFlightRef.current = null;
      dispatch(setGlobalLoading(false));
      setIsFetching(false);
    }
  };

  useEffect(() => {
    // Don't reset filters if prdParam is present (from URL)
    if (!prdParam) {
      handleResetFilters();
    }
  }, [modeValue, prdParam]);

  // mapping object
  const mapThreadItem = (item) => {
    // If merged data from Redux already has conn fields at root, fallback to item.connectionData
    const masterConn = Array.isArray(connectionData)
      ? connectionData.find(c => Number(c.ConnID) === Number(item.ConnID))
      : null;
    const bizName = masterConn?.BizName || "";
    const contactName = item.ContactPerson || masterConn?.ContactName || "";

    let displayName = bizName || contactName || "Unknown Contact";
    const currentConn = {
      bizName: item.bizName?.trim() || item.connectionData?.BizName?.trim() || item.contactName?.trim() || item.connectionData?.ContactName?.trim(),
      contactName: item.contactName ?? item.connectionData?.ContactName,
      cc: item.cc ?? item.connectionData?.CC,
      mobile: item.mobile ?? item.connectionData?.Mobile,
      email: item.email ?? item.connectionData?.Email,
      tasks: item.connectionData?.Tasks,
      lastRecDT: item.lastRec ?? item.connectionData?.LastRecDT,
      Notes: item.connectionData?.Notes // Map Notes for ContactSection
    };
    const currentParsedTasks = safeParseArray(currentConn?.tasks);
    const currentThreadID = item.ThreadID;
    const includesThreadID = (tIDs) =>
      tIDs?.split(",").some((t) => +t.trim() === currentThreadID);

    let nextTask = null;
    for (const task of currentParsedTasks) {
      if (!task?.TIDs || !task?.DT || !includesThreadID(task.TIDs)) continue;
      if (Number(task.Sts ?? task.status ?? 0) !== 1) continue;
      if (!nextTask || task.DT < nextTask.DT) nextTask = task;
    }

    const effectiveNextTaskDT = nextTask?.DT || item.DueDT;

    const thisSinceDT = item.SinceDT;

    if (thisSinceDT) {
      const thisTs = Date.parse(thisSinceDT);


    }

    const segmentName = item.Segment || item.connectionData?.Segment || masterConn?.Segment || "";
    const segmentInfo = segmentsWithColors.find((s) => s.CNm === segmentName);
    const segmentColor = segmentInfo?.Clr || null;

    // Update the shared tracker (in-place for the external reference)
    return {
      threadID: item.ThreadID,
      typeID: item.TypeID,
      agencyID: item.AgencyID,
      connID: item.ConnID,
      mainProduct: item.MainProduct,
      mainQty: item.MainQty,
      model: item.Model,
      pocUserID: item.POCUserID,
      pocUserName: getExecutiveName(item.POCUserID),
      statusID: item.StatusID,
      dueDT: item.DueDT,
      dueDTF: getTableDateOrWeekday(item.DueDT),
      dueDTColor: getColorClass(getTaskColor(item.DueDT)),
      sinceDT: item.SinceDT,
      sinceDTF: getTableDateOrWeekday(item.SinceDT),
      reference: item.Reference,
      bizProspect: item.BizProspect,
      currStageID: item.CurrStageID,
      source: item.Source,
      stageInfo: getStageInfo(item.TypeID, item.CurrStageID),
      nextTaskDT: effectiveNextTaskDT,
      nextTaskDTColor: getTaskColor(effectiveNextTaskDT),
      nextTaskDTF: getTableDateOrWeekday(effectiveNextTaskDT),
      nextTaskNote: nextTask?.Txt,
      nextTaskTyp: nextTask?.Typ,
      lastRecordDT: currentConn?.lastRecDT,
      lastRecordDTColor: getLastDateColor(currentConn?.lastRecDT),
      lastRecordDTF: getTableDateOrWeekday(currentConn?.lastRecDT),
      lastRecordDTAgo: timeAgo(currentConn?.lastRecDT),
      notes: item.Notes,
      thrFile: item.ThrFile || item.thrFile || item.thrfile,
      segment: segmentName,
      segmentColor: segmentColor,
      connectionData: {
        bizName: bizName,
        contactName: contactName,
        displayName: displayName,
        mobile: masterConn?.Mobile || "",
        cc: masterConn?.CC || "",
        email: masterConn?.Email || "",
        connType: masterConn?.ConnType || "",
        segment: segmentName,
        segmentColor: segmentColor,
      },
      feedbackNotes: item.FdbkNotes,
      feedbackScore: item.FdbkScore,
      statusID: item.StatusID,
      failureReason: item.FailureReason, // The Rejected reason
      disqlReason: item.DisqlReason,     // The Disqualified reason
    };
  };

  const processedThreads = useMemo(() => {
    // Pull directly from Redux Store
    const baseData = threadData || [];

    return baseData
      .map(mapThreadItem)
      .filter((thread) => {
        // Perform ONLY non-API filtering here (Executive, Search, local Stage)
        const matchesSearch = !searchTerm ||
          thread.connectionData?.bizName?.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesExec = selectedFilters.assignedTo === "all" ||
          String(thread.pocUserID) === String(selectedFilters.assignedTo);

        return matchesSearch && matchesExec;
      });
  }, [threadData, searchTerm, selectedFilters.assignedTo]);
  // Only update when data is ready
  useEffect(() => {
    setThreads(processedThreads);
  }, [processedThreads]);

  // Remove the standalone loadData() calls in other effects and use this:
  useEffect(() => {
    const handleThreadsUpdated = () => {
      prevFiltersRef.current = {};
      setRefreshTrigger((prev) => prev + 1);
    };
    window.addEventListener("threads-updated", handleThreadsUpdated);
    return () => {
      window.removeEventListener("threads-updated", handleThreadsUpdated);
    };
  }, []);

  useEffect(() => {
    // Skip loadData only if monthParam exists but hasn't been processed yet
    const shouldSkipForMonthParam = monthParam && !monthParamProcessedRef.current;

    if ((tID || selectedFilters.types) && !shouldSkipForMonthParam) {
      if (refreshTrigger > 0 || (embedded && parentActiveTab === 'sales')) {
        prevFiltersRef.current = {};
      }
      loadData();
    }
  }, [
    tID,
    selectedFilters.dateRange,
    selectedFilters.otherMonth,
    selectedFilters.otherQuarter,
    selectedFilters.otherYear,
    selectedFilters.status,
    refreshTrigger,
    monthParam,
    selectedFilters.selectDate,
    selectedFilters.selectedDate,
    parentActiveTab
  ]);
  // Apply filters
  useEffect(() => {
    setIsLoading(true);
    applyFilters();
    setIsLoading(false);
  }, [threads, selectedFilters]);


  useEffect(() => {
    applyFilters();
  }, [
    sourceThreads, // Triggers when API data returns
    selectedFilters.assignedTo,
    selectedFilters.connID,
    selectedFilters.types,
    selectedFilters.status, // Also call here to apply the field-logic locally
    selectedFilters.selectDate,
    selectedFilters.selectedDate
  ]);
  // You can keep this to ensure filteredThreads stay in sync with the source
  // on the very first render, but the sourceThreads logic above does the heavy lifting.
  useEffect(() => {
    if (initialSortApplied && filteredThreads.length > 0) {
      // If no manual sort has been touched yet, ensure we use the default
      if (sortConfig.key === "sinceDT" && sortConfig.direction === "desc") {
        applyFilters(); // This will trigger sortData internally
      }
    }
  }, [initialSortApplied]);

  //managing paramConnID, stage and status params
  useEffect(() => {
    const conn = searchParams.get("cid");
    const stage = searchParams.get("stg");
    const statusID = searchParams.get("sID");
    const reasonID = searchParams.get("rID");
    const reasonType = searchParams.get("rTyp");
    const dateRange = searchParams.get("p") || searchParams.get("dateRange");
    const otherMonth = searchParams.get("om") || searchParams.get("otherMonth");
    const otherQuarter =
      searchParams.get("oq") || searchParams.get("otherQuarter");
    const otherYear = searchParams.get("oy") || searchParams.get("otherYear");
    const executiveID = searchParams.get("eID") || searchParams.get("eid") || searchParams.get("assignedTo");
    const productID = searchParams.get("pID") || searchParams.get("pid") || searchParams.get("productID");

    if (conn && conn !== paramConnID) {
      setParamConnID(conn);
    }
    if (stage && stage !== paramStage) {
      setParamStage(Number(stage));
    }
    if (statusID && statusID !== paramStatus) {
      setParamStatus(statusID);
    }
    if (reasonID && reasonID !== paramReasonID) {
      setParamReasonID(reasonID);
    }
    if (reasonType && reasonType !== paramReasonType) {
      setParamReasonType(reasonType);
    }
    if (dateRange && dateRange !== paramDateRange) {
      setParamDateRange(dateRange);
    }
    if (otherMonth && otherMonth !== paramOtherMonth) {
      setParamOtherMonth(otherMonth);
    }
    if (otherQuarter && otherQuarter !== paramOtherQuarter) {
      setParamOtherQuarter(otherQuarter);
    }
    if (otherYear && otherYear !== paramOtherYear) {
      setParamOtherYear(otherYear);
    }
    if (executiveID && executiveID !== paramExecutiveID) {
      setParamExecutiveID(executiveID);
    }
    if (productID && productID !== paramProductID) {
      setParamProductID(productID);
    }
  }, [location.search]);

  useEffect(() => {
    const paramMap = {
      connID: paramConnID,
      stage: paramStage,
      status: paramStatus,
      reasonID: paramReasonID,
      reasonType: paramReasonType,
      dateRange: paramDateRange,
      otherMonth: paramOtherMonth,
      otherQuarter: paramOtherQuarter,
      otherYear: paramOtherYear,
      assignedTo: paramExecutiveID,
      productID: paramProductID,
    };

    Object.entries(paramMap).forEach(([key, value]) => {
      if (
        value !== "" &&
        value !== null &&
        value !== undefined &&
        value !== 0
      ) {
        setSelectedFilters((prev) => ({
          ...prev,
          [key]: value,
        }));
        setTempFilters((prev) => ({
          ...prev,
          [key]: value,
        }));
        if (!isMobile) {
          setIsFilterSelected(true);
        }
      }
    });
  }, [
    paramConnID,
    paramStage,
    paramStatus,
    paramReasonID,
    paramReasonType,
    paramDateRange,
    paramOtherMonth,
    paramOtherQuarter,
    paramOtherYear,
    paramExecutiveID,
    paramProductID,
    isMobile,
  ]);

  const isThreadsEmpty = () => !threads || threads.length === 0;

  const resetSort = () => {
    setSortConfig({ key: "sinceDT", direction: "desc" });
  };

  const getNestedValue = (obj, path) => {
    return path.split(".").reduce((o, k) => (o ? o[k] : ""), obj);
  };

  const sortData = (key, dir) => {
    if (!key && !dir) return;
    const actualKey = sortKeyMap[key] || key;
    let direction =
      dir ??
      (sortConfig.key === actualKey && sortConfig.direction === "asc"
        ? "desc"
        : "asc");
    setSortConfig({ key: actualKey, direction });
    const sorted = [...filteredThreads].sort((a, b) => {
      let lowerVal, upperVal;
      const now = Date.now();

      // InactivePeriod (days since lastRecordDT)
      if (key === "inactive") {
        const getDaysSince = (str) => {
          const dt = new Date(str);
          return isNaN(dt)
            ? Infinity
            : Math.floor((now - dt.getTime()) / (1000 * 60 * 60 * 24));
        };
        lowerVal = getDaysSince(a.lastRecordDT);
        upperVal = getDaysSince(b.lastRecordDT);
        return direction === "asc" ? lowerVal - upperVal : upperVal - lowerVal;
      }
      lowerVal = getNestedValue(a, actualKey);
      upperVal = getNestedValue(b, actualKey);

      // Handle numeric fields
      if (["bizProspect"].includes(key)) {
        lowerVal = Number(lowerVal) || 0;
        upperVal = Number(upperVal) || 0;
        return direction === "asc" ? lowerVal - upperVal : upperVal - lowerVal;
      }

      // Handle date fields
      if (["sinceDT", "dueDT", "lastRecordDT", "nextTaskDT"].includes(key)) {
        lowerVal = lowerVal ? new Date(lowerVal).getTime() : 0;
        upperVal = upperVal ? new Date(upperVal).getTime() : 0;
        return direction === "asc" ? lowerVal - upperVal : upperVal - lowerVal;
      }

      // Handle string fields
      lowerVal = lowerVal?.toString().toLowerCase() || "";
      upperVal = upperVal?.toString().toLowerCase() || "";

      return direction === "asc"
        ? lowerVal.localeCompare(upperVal)
        : upperVal.localeCompare(lowerVal);
    });
    setFilteredThreads(sorted);
    setPreSearchThreads(sorted);
  };

  const isSameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  const getDateRangeFilter = (value, passedDt) => {
    if (!passedDt) return false;
    const todayDT = new Date();
    const currDT = new Date(passedDt);

    switch (value) {
      case "Overdue":
        return currDT < todayDT;
      case "Yesterday":
        const yest = new Date(todayDT);
        yest.setDate(todayDT.getDate() - 1);
        return isSameDay(currDT, yest);
      case "Today":
        return isSameDay(currDT, todayDT);
      case "Tomorrow":
        const tmr = new Date(todayDT);
        tmr.setDate(todayDT.getDate() + 1);
        return isSameDay(currDT, tmr);
      case "This week":
        const endOfWeek = new Date(todayDT);
        endOfWeek.setDate(todayDT.getDate() + (6 - todayDT.getDay()));
        return currDT >= todayDT && currDT <= endOfWeek;
      case "Next 7 days":
        const next7 = new Date(todayDT);
        next7.setDate(todayDT.getDate() + 7);
        return currDT >= todayDT && currDT <= next7;
      case "Next 30 days":
        const next30 = new Date(todayDT);
        next30.setDate(todayDT.getDate() + 30);
        return currDT >= todayDT && currDT <= next30;
      case "Last 7 days":
        const last7 = new Date(todayDT);
        last7.setDate(todayDT.getDate() - 7);
        return currDT >= last7 && currDT <= todayDT;
      case "Last month":
        const lastMonth = new Date(todayDT);
        lastMonth.setMonth(todayDT.getMonth() - 1);
        return currDT >= lastMonth && currDT <= todayDT;
      default:
        return true;
    }
  };

  useEffect(() => {
    const term = searchTerm.trim().toLowerCase();

    if (!term) {
      setFilteredThreads(preSearchThreads);
      setCurrentPage(1);
      return;
    }

    const filtered = preSearchThreads.filter((thread) => {
      const data = thread.connectionData || {};
      const fieldsToSearch = [
        data.contactName,
        data.bizName,
        data.email,
        data.mobile,
        thread.mainProduct,
        thread.product,
        thread.bizProspect?.toString(),
        thread.source,
        thread.Source,
        thread.src,
        thread.Src,
      ];

      return fieldsToSearch
        .filter(Boolean)
        .some((field) => field.toString().toLowerCase().includes(term));
    });

    setFilteredThreads(filtered);
    setCurrentPage(1);
  }, [searchTerm, preSearchThreads]);

  useEffect(() => {
    const handleHeaderFilterClick = () => {
      setIsFilterSelected(true);
      setIsDrawerOpen(true);
    };

    window.addEventListener("open-threads-filter", handleHeaderFilterClick);
    return () => window.removeEventListener("open-threads-filter", handleHeaderFilterClick);
  }, []);

  const getReasonText = (threadTypeID, reasonID, reasonType) => {
    if (!reasonID || !reasonType) return null;

    const reasonsArray =
      reasonType === "DSQL" ? disqlReasons : rejectionReasons;
    const threadReasons = reasonsArray.find((r) => String(r.TID) === String(threadTypeID));

    if (!threadReasons) return null;

    const parsedReasons = JSON.parse(threadReasons.Rsns);
    const reason = parsedReasons.find((r) => String(r.RID) === String(reasonID));
    return reason ? reason.R : null;
  };

  const applyFilters = () => {
    // Always start from the base data fetched from API to allow "un-filtering"
    let filtered = [...sourceThreads];

    // 1. Strict Status & Reason Logic
    const filterStatus = String(selectedFilters.status);

    if (filterStatus === "3") {
      // REJECTED: Status is 3 AND FailureReason has a value
      filtered = filtered.filter(t => t.statusID === 3 && t.failureReason && t.failureReason.trim() !== "");
      if (selectedFilters.reasonID && selectedFilters.reasonID !== "all") {
        const reasonText = getReasonText(selectedFilters.types, selectedFilters.reasonID, "REJ");
        if (reasonText) {
          filtered = filtered.filter(t => t.failureReason === reasonText);
        }
      }
    } else if (filterStatus === "4") {
      // DISQUALIFIED: Status is 3 (per your requirement) AND DisqlReason has a value
      filtered = filtered.filter(t => t.statusID === 3 && t.disqlReason && t.disqlReason.trim() !== "");
      if (selectedFilters.reasonID && selectedFilters.reasonID !== "all") {
        const reasonText = getReasonText(selectedFilters.types, selectedFilters.reasonID, "DSQL");
        if (reasonText) {
          filtered = filtered.filter(t => t.disqlReason === reasonText);
        }
      }
    } else if (filterStatus === "2") {
      // CONVERTED
      filtered = filtered.filter(t => t.statusID === 2);
    } else if (filterStatus === "0") {
      // OPEN
      filtered = filtered.filter(t => t.statusID === 0);
    }

    // 2. Executive Filter (Frontend Only)
    if (selectedFilters.assignedTo !== "all") {
      filtered = filtered.filter(t =>
        selectedFilters.assignedTo === "unassigned"
          ? !t.pocUserID
          : String(t.pocUserID) === String(selectedFilters.assignedTo)
      );
    }

    // 3. Contact Filter (Frontend Only)
    if (selectedFilters.connID !== "all") {
      filtered = filtered.filter(t => String(t.connID) === String(selectedFilters.connID));
    }

    // 4. Thread Type Filter
    if (selectedFilters.types !== "all") {
      const activeTypes = selectedFilters.types.split(",").map(Number);
      filtered = filtered.filter(t => activeTypes.includes(Number(t.typeID)));
    }

    // 4.5 Product Filter (Frontend Only)
    if (selectedFilters.productID && selectedFilters.productID !== "all") {
      const targetProduct = dropdownProducts.find(p => String(p.id || p.PID) === String(selectedFilters.productID));
      if (targetProduct) {
        const targetName = (targetProduct.name || targetProduct.PNm || "").trim().toLowerCase();
        filtered = filtered.filter(t =>
          (t.mainProduct || "").trim().toLowerCase() === targetName ||
          (t.product || "").trim().toLowerCase() === targetName
        );
      }
    }

    // 5. Select Date Filter (NEW) - Filter by specific date (from prd param or manual selection)
    if (selectedFilters.selectDate && selectedFilters.selectedDate) {
      const selectedDateStr = selectedFilters.selectedDate;
      const beforeCount = filtered.length;
      filtered = filtered.filter(t => {
        if (!t.sinceDT) return false;
        const tDateStr = t.sinceDT.split('T')[0];
        const matches = tDateStr === selectedDateStr;
        return matches;
      });
    }

    setFilteredThreads(filtered);
    setPreSearchThreads(filtered); // Update this so Search works on the filtered results
  };

  // Pagination
  const totalPages = Math.ceil(filteredThreads.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;

  // Always sort before slicing for pagination
  // First filter by typeID regardless of device
  // shows the current data via currentThreads
  const currentThreads = useMemo(() => {
    // Since the API already returned the correct data for the period,
    // we just need to handle pagination and sorting here.
    let data = [...filteredThreads];

    // Apply sorting based on sortConfig
    if (sortConfig.key && sortConfig.direction) {
      data = data.sort((a, b) => {
        let lowerVal, upperVal;
        const now = Date.now();

        // InactivePeriod (days since lastRecordDT)
        if (sortConfig.key === "inactive") {
          const getDaysSince = (str) => {
            const dt = new Date(str);
            return isNaN(dt)
              ? Infinity
              : Math.floor((now - dt.getTime()) / (1000 * 60 * 60 * 24));
          };
          lowerVal = getDaysSince(a.lastRecordDT);
          upperVal = getDaysSince(b.lastRecordDT);
          return sortConfig.direction === "asc" ? lowerVal - upperVal : upperVal - lowerVal;
        }

        lowerVal = getNestedValue(a, sortConfig.key);
        upperVal = getNestedValue(b, sortConfig.key);

        // Handle numeric fields
        if (["bizProspect"].includes(sortConfig.key)) {
          lowerVal = Number(lowerVal) || 0;
          upperVal = Number(upperVal) || 0;
          return sortConfig.direction === "asc" ? lowerVal - upperVal : upperVal - lowerVal;
        }

        // Handle date fields
        if (["sinceDT", "dueDT", "lastRecordDT", "nextTaskDT"].includes(sortConfig.key)) {
          lowerVal = lowerVal ? new Date(lowerVal).getTime() : 0;
          upperVal = upperVal ? new Date(upperVal).getTime() : 0;
          return sortConfig.direction === "asc" ? lowerVal - upperVal : upperVal - lowerVal;
        }

        // Handle string fields
        lowerVal = lowerVal?.toString().toLowerCase() || "";
        upperVal = upperVal?.toString().toLowerCase() || "";

        return sortConfig.direction === "asc"
          ? lowerVal.localeCompare(upperVal)
          : upperVal.localeCompare(lowerVal);
      });
    }

    const hasDecimalBizProspect = data.some(
      d => Number(d.bizProspect) % 1 !== 0
    );

    setIsDecimal(hasDecimalBizProspect);

    if (isMobile) return data;

    const startIndex = (currentPage - 1) * rowsPerPage;
    return data.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredThreads, currentPage, rowsPerPage, isMobile, sortConfig]);

  const rowVirtualizer = useVirtualizer({
    count: currentThreads.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 140, // Estimated row/card height in px
    overscan: 5,
  });

  // Re-measure virtualizer when tab becomes active/visible again
  useEffect(() => {
    if (!embedded || parentActiveTab === 'sales' || parentActiveTab === '') {
      const timer = setTimeout(() => {
        if (rowVirtualizer && typeof rowVirtualizer.measure === 'function') {
          rowVirtualizer.measure();
        }
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [parentActiveTab, embedded, currentThreads.length]);

  // Cancel/Close Drawer of filters so data wont be affected
  const handleCancel = () => {
    setTempFilters(selectedFilters); // Revert changes
    setIsFilterSelected(false);
    setIsDrawerOpen(false); // Close the drawer
    setTempsortConfig(sortConfig);
  };

  const handleResetFilters = () => {
    // Get month filters from URL parameter to preserve them during reset
    const monthFilters = getMonthFilterFromNumber(monthParam);

    const cleared = {
      stage: "all",
      assignedTo: "all",
      receivedOn: "all",
      dueOn: "all",
      lastRecordOn: "all",
      nextTaskOn: "all",
      types: tID && Number(tID) !== 0 ? String(tID) : "123456",
      connID: "all",
      dateRange: monthFilters.dateRange,
      finYear: "all",
      otherMonth: monthFilters.otherMonth,
      otherQuarter: "all",
      otherYear: "all",
      status: "all",
      reasonID: "all", // ADD THIS
      reasonType: "all",
      selectDate: false,
      selectedDate: null,
      productID: "all",
    };
    resetSort();
    setSelectedFilters(cleared);
    setSearchTerm("");
    setIsDrawerOpen(false);
    setTempFilters(cleared);
    setTempsortConfig({ key: null, direction: null });
    // setSearchTerm("");
    // setSortConfig({ key: null, direction: null });

    prevFiltersRef.current = {};
    setFilteredThreads(threads);
    setPreSearchThreads(threads);
  };
  const renderEmptyState = () => {
    const typeID = Number(threadTypeInfo.typeID);

    // Configuration Map based on tID
    const emptyStateConfigs = {
      1: {
        title: "Inquiry History",
        description: "Track monthly inquiry records and trends across all sales channels",
        icon: History,
        subText: "No data available right now."
      },
      2: {
        title: isInqOnlyMode ? "Sales" : "Sales History",
        description: "View monthly sales records and patterns for revenue analysis",
        icon: ShoppingCart,
        subText: "No data available right now."
      },
      3: {
        title: "Support History",
        description: "Analyze monthly support ticket records and resolution trends",
        icon: HeadphonesIcon,
        subText: "No data available right now."
      },
      4: {
        title: "Purchase Inquiry History",
        description: "Track monthly inquiry records and trends across all purchase channels",
        icon: History,
        subText: "No data available right now."
      },
      5: {
        title: "Purchase Sales History",
        description: "View monthly sales records and patterns for procurement analysis",
        icon: ShoppingCart,
        subText: "No data available right now."
      },
      6: {
        title: "Complaint History",
        description: "Analyze monthly supplier complaint records and resolution trends",
        icon: HeadphonesIcon,
        subText: "No data available right now."
      },
      default: {
        title: "Record History",
        description: "Track and analyze your monthly records and business trends",
        icon: ClipboardList,
        subText: "No data available right now."
      }
    };

    const config = emptyStateConfigs[typeID] || emptyStateConfigs.default;
    const IconComponent = config.icon;

    return (
      <div className="w-full">
        <div className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg bg-white shadow-sm w-full md:w-fit md:min-w-[500px]">
          <div className="flex-shrink-0 p-2 bg-gray-50 rounded-md">
            <IconComponent className="h-6 w-6 text-gray-400" strokeWidth={2} />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-sm font-semibold text-gray-700 leading-tight">
              {config.description}
            </span>
            <span className="text-xs font-medium text-gray-400">
              {config.subText}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const CardSkeleton = () => (
    <div className="bg-white sahi-radius shadow-sm border border-gray-200 p-3 mb-2 w-full animate-pulse flex flex-col gap-2.5">
      <div className="flex justify-between items-center">
        <div className="h-4 bg-gray-200 rounded w-1/3"></div>
        <div className="h-4 bg-gray-200 rounded w-16"></div>
      </div>
      <div className="flex justify-between items-center">
        <div className="h-3.5 bg-gray-200 rounded w-1/4"></div>
        <div className="h-3.5 bg-gray-200 rounded w-20"></div>
      </div>
      <div className="flex justify-between items-center pt-2 border-t border-gray-100 mt-1">
        <div className="h-3 bg-gray-200 rounded w-1/2"></div>
        <div className="flex gap-2">
          <div className="h-6 w-6 bg-gray-200 rounded-full"></div>
          <div className="h-6 w-6 bg-gray-200 rounded-full"></div>
        </div>
      </div>
    </div>
  );

  const ListSkeleton = () => (
    <div className="flex flex-col w-full gap-1">
      <CardSkeleton />
      <CardSkeleton />
      <CardSkeleton />
    </div>
  );

  const TableSkeleton = () => {
    const headers = getFilteredHeaders();

    return (
      <>
        <Card className="bg-sahi-white hidden lg:block !pt-0 overflow-hidden">
          <CardContent className="px-0 !sahi-radius">
            <Table className="min-w-full table-auto">
              <TableHeader>
                <TableRow className="bg-gray-50/50 py-1.5 h-11 border-b">
                  {headers.map(({ key, label, className = "", style = {} }) => {
                    const isAmount = key === "bizProspect" || (key === "status" && selectedFilters.status === "2");
                    return (
                      <TableHead
                        key={key}
                        className={cn(
                          "table-header-text font-semibold text-black",
                          isAmount ? "text-right pr-6" : "text-left pl-4"
                        )}
                        style={style}
                      >
                        <div className={cn("flex items-center", isAmount ? "justify-end" : "justify-start", className)}>
                          {label}
                        </div>
                      </TableHead>
                    );
                  })}
                  <TableHead className="w-40 text-right pr-4"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Array.from({ length: 6 }).map((_, rowIndex) => (
                  <TableRow
                    key={rowIndex}
                    className="h-10 align-middle border-b border-gray-100"
                  >
                    {headers.map((header) => {
                      const isAmountCol = header.key === "bizProspect" || (header.key === "status" && selectedFilters.status === "2");
                      const cellClass = isAmountCol ? "text-right pr-6" : "text-left pl-4";

                      if (header.key === "type") {
                        return (
                          <TableCell key={header.key} className="w-[3vw] pl-4 text-left">
                            <div className="h-5 w-5 bg-gray-200 rounded-full animate-pulse"></div>
                          </TableCell>
                        );
                      }

                      if (header.key === "reference") {
                        const widths = ["w-14", "w-16", "w-12", "w-14", "w-16", "w-12"];
                        return (
                          <TableCell key={header.key} className={cellClass}>
                            <div className={cn("h-3.5 bg-gray-200 rounded animate-pulse", widths[rowIndex % widths.length])}></div>
                          </TableCell>
                        );
                      }

                      if (header.key === "bizName") {
                        const widths = ["w-36", "w-48", "w-32", "w-44", "w-36", "w-28"];
                        return (
                          <TableCell key={header.key} className={cn("max-w-[250px]", cellClass)}>
                            <div className={cn("h-3.5 bg-gray-200 rounded animate-pulse", widths[rowIndex % widths.length])}></div>
                          </TableCell>
                        );
                      }

                      if (header.key === "mainProduct") {
                        const widths = ["w-40", "w-32", "w-36", "w-28", "w-44", "w-36"];
                        return (
                          <TableCell key={header.key} className={cellClass}>
                            <div className={cn("h-3.5 bg-gray-200 rounded animate-pulse", widths[rowIndex % widths.length])}></div>
                          </TableCell>
                        );
                      }

                      if (isAmountCol) {
                        const widths = ["w-16", "w-20", "w-14", "w-24", "w-16", "w-20"];
                        return (
                          <TableCell key={header.key} className={cellClass}>
                            <div className={cn("h-3.5 bg-gray-200 rounded ml-auto animate-pulse", widths[rowIndex % widths.length])}></div>
                          </TableCell>
                        );
                      }

                      if (header.key === "sinceDT" || header.key === "dueDT") {
                        return (
                          <TableCell key={header.key} className={cellClass}>
                            <div className={cn("h-3.5 bg-gray-200 rounded animate-pulse", ["w-14", "w-16", "w-14", "w-16", "w-12", "w-14"][rowIndex % 6])}></div>
                          </TableCell>
                        );
                      }

                      if (header.key === "pocUserName") {
                        const widths = ["w-28", "w-24", "w-32", "w-20", "w-28", "w-24"];
                        return (
                          <TableCell key={header.key} className={cellClass}>
                            <div className={cn("h-3.5 bg-gray-200 rounded animate-pulse", widths[rowIndex % widths.length])}></div>
                          </TableCell>
                        );
                      }

                      return (
                        <TableCell key={header.key} className={cellClass}>
                          <div className="h-3.5 bg-gray-200 rounded w-20 animate-pulse"></div>
                        </TableCell>
                      );
                    })}

                    {/* STATIC ACTIONS COLUMN */}
                    <TableCell className="text-right pr-4 w-40">
                      <div className="flex justify-end items-center gap-3 pr-2">
                        <div className="h-4 w-4 bg-gray-200 rounded-full animate-pulse"></div>
                        <div className="h-1 w-2 bg-gray-200 rounded animate-pulse"></div>
                        <div className="h-4 w-4 bg-gray-200 rounded-full animate-pulse"></div>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Pagination Skeleton */}
        <div className="hidden lg:flex justify-between items-center mt-6 animate-pulse">
          <div className="flex gap-2 items-center">
            {[10, 25, 50, 100].map((size) => (
              <div key={size} className="h-8 w-10 bg-gray-200 rounded sahi-radius"></div>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <div className="h-4 w-20 bg-gray-200 rounded"></div>
            <div className="flex gap-1">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-8 w-8 bg-gray-200 rounded sahi-radius"></div>
              ))}
            </div>
          </div>
        </div>
      </>
    );
  };

  const MobileCard = ({ thread, threadTypeInfo, threadTypes }) => {
    const isTIDZero = threadTypeInfo.typeID === 0;
    const agencyData = getAgencyData();
    const countryID = agencyData?.CountryID || "";
    const isExpanded = expandedThreadID === thread.threadID;

    // Check if we have contact info for footer
    const hasContactInfo = thread.connectionData?.mobile || thread.connectionData?.email;

    // Memoize thread type meta calculation
    const threadTypeMeta = useMemo(() => {
      if (!isTIDZero) return null;
      return getThreadTypeMeta(thread.typeID, threadTypes);
    }, [isTIDZero, thread.typeID, threadTypes]);

    // Permissions Logic: ThrActions 1 or 2 allows editing threads
    const canEditPerm = thrActions >= 1 && thrActions <= 2;
    const shouldShowEditButton = canEditPerm;
    const shouldShowViewContactButton = !!(thread.connID || thread.connectionData?.connID);

    const handleCardClick = () => {
      setExpandedThreadID(prev => (prev === thread.threadID ? null : thread.threadID));
    };

    return (
      <div
        className={cn(
          "bg-sahi-white sahi-radius shadow-sm border mb-2 w-full f-col transition-all duration-200 cursor-pointer"
        )}
        onClick={handleCardClick}
      >
        {/* === Header Section === */}
        <div className={cn("f-col gap-1 p-2.5", !isExpanded && "pb-3")}>
          <div className="f-row justify-between gap-2">
            <div className="f-col items-center justify-between min-w-0 flex-1">
              <div className="f-col min-w-0 w-full">
                <div className="flex items-center gap-1.5 min-w-0">
                  <span
                    className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: thread.segmentColor || thread.connectionData?.segmentColor || "#d1d5db" }}
                  />
                  <p
                    className="font-semibold text-text-color text-sm truncate max-w-full"
                    data-tooltip-id={`bizName-${thread.threadID}`}
                    data-tooltip-content={
                      thread.connectionData?.bizName ||
                      thread.connectionData?.contactName
                    }
                  >
                    {thread.connectionData?.bizName?.length > 25
                      ? thread.connectionData.bizName.slice(0, 25) + "..."
                      : thread.connectionData?.bizName ||
                      thread.connectionData?.contactName}
                  </p>
                </div>
                <Tooltip id={`bizName-${thread.threadID}`} place="top" />
                {thread.connectionData?.bizName &&
                  thread.connectionData?.bizName !== "-" && (
                    <p className="text-xs text-sub-text-color mt-1 truncate">
                      {thread.connectionData?.contactName || "--"}
                    </p>
                  )}
              </div>
            </div>

            <div className="f-col items-end space-y-1 text-right shrink-0">
              {/* Status Badge */}
              {isTIDZero ? (
                threadTypeMeta && (
                  <span
                    className={`${threadTypeMeta.colorClass} f-row justify-between gap-1 items-center text-xs font-semibold`}
                  >
                    <ThreadSahiIcon
                      tID={thread.typeID}
                      className="h-3 w-3 xs:h-4 xs:w-4 shrink-0"
                    />
                  </span>
                )
              ) : (
                <div className="flex flex-col items-end gap-1">
                  {[1, 3, 4].includes(Number(thread.typeID)) && (
                    <Badge className="!bg-sahi-badge-bg-color !text-sahi-teal !font-semibold !text-xs !px-2 !py-1 !sahi-radius">
                      {thread.statusID === 0 ? "Open" :
                        Number(thread.typeID) === 3 ? "Closed" :
                          thread.statusID === 2 ? "Closed" :
                            thread.failureReason ? "Rejected" :
                              thread.disqlReason ? "Disqualified" : "Closed"}
                    </Badge>
                  )}
                  {selectedFilters.status === "3" && thread.failureReason && (
                    <span className="text-xs text-sahi-red font-medium">{thread.failureReason}</span>
                  )}
                  {selectedFilters.status === "4" && thread.disqlReason && (
                    <span className="text-xs text-sahi-red font-medium">{thread.disqlReason}</span>
                  )}
                </div>
              )}

              {/* Amount (Row 1 Right) */}
              {((Number(threadTypeInfo.typeID) !== 1) || (selectedFilters.status === "2")) && (
                <span className="text-xs text-[var(--sahi-green)] font-semibold align-bottom shrink-0 whitespace-nowrap">
                  {thread.bizProspect > 0
                    ? `${currencySymbol} ${Number(thread.bizProspect).toLocaleString(Number(countryID) === 1 ? 'en-IN' : 'en-US')}`
                    : "--"}
                </span>
              )}

              {/* Order Ref (Row 2 Right, beneath amount) */}
              {thread.reference ? (
                <span className="text-xs text-sub-text-color align-bottom shrink-0 whitespace-nowrap font-medium">
                  # {thread.reference}
                </span>
              ) : null}
            </div>
          </div>

          <div className="f-row justify-between items-center mt-1">
            {thread.mainProduct && (
              <p className="text-xs text-sub-text-color truncate pr-2">
                <span className="font-medium text-text-color">{thread.mainProduct}</span>
                {Number(thread.mainQty) > 1 && <span className="text-gray-500 ml-1 font-medium">x {thread.mainQty}</span>}
              </p>
            )}

            {thread.sinceDT ? (
              <span className="text-xs text-sub-text-color truncate ml-auto shrink-0">
                <strong>{thread.sinceDTF}</strong>
              </span>
            ) : (
              <span className="text-xs text-sub-text-color truncate ml-auto shrink-0">
                <strong>{thread.lastRecordDTF}</strong>
              </span>
            )}
          </div>
        </div>

        {/* === Expanded Details Section with smooth transition === */}
        <div
          className={cn(
            "grid transition-all duration-200 ease-in-out border-gray-200",
            isExpanded ? "grid-rows-[1fr] opacity-100 border-t border-dashed" : "grid-rows-[0fr] opacity-0 border-t-0"
          )}
        >
          <div className="overflow-hidden">
            <div className="flex flex-col gap-2 px-2.5 pb-2.5 pt-2">
              {/* Meta Badges Row */}
              <div className="flex flex-wrap items-center gap-2 text-xs">
                {thread.model && (
                  <div className="inline-flex items-center gap-1.5 bg-[#f3f4f6] px-3 py-1.5 rounded text-xs text-text-color">
                    <span className="text-gray-500 font-normal">Model:</span>
                    <span className="font-semibold text-gray-900">{thread.model}</span>
                  </div>
                )}

                {thread.bizProspect > 0 && (
                  <div className="inline-flex items-center gap-1.5 bg-[#f3f4f6] px-3 py-1.5 rounded text-xs text-text-color">
                    <span className="text-gray-500 font-normal">Value:</span>
                    <span className="font-semibold text-gray-900">
                      {currencySymbol} {Number(thread.bizProspect).toLocaleString(Number(countryID) === 1 ? 'en-IN' : 'en-US')}
                    </span>
                  </div>
                )}

                {(thread.pocUserName || thread.pocUserID) && (
                  <div className="inline-flex items-center gap-1.5 bg-[#f3f4f6] px-3 py-1.5 rounded text-xs text-text-color">
                    <span className="text-gray-500 font-normal">Credit to</span>
                    <span className="font-semibold text-gray-900">{thread.pocUserName || getExecutiveName(thread.pocUserID)}</span>
                  </div>
                )}

                {thread.reference && (
                  <div className="inline-flex items-center gap-1.5 bg-[#f3f4f6] px-3 py-1.5 rounded text-xs text-text-color">
                    <span className="text-gray-500 font-normal">Ref:</span>
                    <span className="font-semibold text-gray-900">{thread.reference}</span>
                  </div>
                )}

                {thread.thrFile && (
                  <div
                    className="inline-flex items-center gap-1.5 bg-[#f3f4f6] px-3 py-1.5 rounded text-xs text-sahi-blue cursor-pointer hover:underline"
                    onClick={(e) => {
                      e.stopPropagation();
                      setPreviewFilePath(thread.thrFile);
                    }}
                  >
                    <Paperclip className="h-3 w-3 flex-shrink-0" />
                    <span className="font-semibold">{Number(thread.typeID) === 2 || Number(thread.typeID) === 5 ? "Document" : "Attachment"}</span>
                  </div>
                )}
              </div>

              {/* Notes Section */}
              {thread.notes && thread.notes.trim() !== "" && (
                <div className="bg-[#f3f4f6] px-3 py-2 rounded text-xs text-text-color leading-relaxed">
                  <span className="text-gray-500 font-normal mr-1.5">Notes:</span>
                  <span className="font-semibold text-gray-900 whitespace-pre-wrap break-words">{thread.notes}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* === Footer Section (Visible only when card is expanded) === */}
        {isExpanded && (
          <div className="flex justify-between items-center text-xs border-t border-[#f1f1f1] w-full mobile-tile-footer-layout px-3 py-2 bg-white rounded-b-md">
            <div className="flex flex-wrap gap-2 items-center">
              {/* Left slot placeholder */}
            </div>

            <div className="flex items-center gap-4">
              {Number(thread.typeID) === 2 && Number(thread.statusID) === 2 && Boolean(thread.connectionData?.mobile) && (
                <button
                  type="button"
                  title="Send feedback request on WhatsApp"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFdbkRequest(thread);
                  }}
                  className="h-7 w-7 p-0 flex items-center justify-center rounded-md hover:bg-sahi-teal/10 text-sahi-teal hover:shadow-sm transform-gpu transition-transform transition-colors duration-200 ease-out hover:scale-110 active:scale-95"
                >
                  <MessageSquareText className="h-[18px] w-[18px]" />
                </button>
              )}

              {hasContactInfo && (
                <ContactActions 
                  connectionData={thread?.connectionData} 
                  connID={thread?.connID} 
                  showEmail={false} 
                  showDashes={false} 
                  stageID={thread?.currStageID}
                />
              )}

              {isExpanded && (
                <>
                  {shouldShowEditButton && (
                    <button
                      type="button"
                      title="Edit Order"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedThread(thread);
                        setSelectedThreadTypeFromDropdown(thread.typeID || 2);
                        setThreadEntryOpen(true);
                      }}
                      className="h-7 w-7 p-0 flex items-center justify-center rounded-md hover:bg-sahi-dark-blue/10 text-sahi-dark-blue hover:shadow-sm transform-gpu transition-transform transition-colors duration-200 ease-out hover:scale-110 active:scale-95"
                    >
                      <Pencil className="h-[18px] w-[18px]" />
                    </button>
                  )}

                  {shouldShowViewContactButton && (
                    <button
                      type="button"
                      title="View Contact"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedConnID(thread.connID || thread.connectionData?.connID);
                        setSelectedThread(thread);
                        setIsMobileConnDetailOpen(true);
                      }}
                      className="h-7 w-7 p-0 flex items-center justify-center rounded-md hover:bg-zinc-100 text-zinc-500 hover:shadow-sm transform-gpu transition-transform transition-colors duration-200 ease-out hover:scale-110 active:scale-95"
                    >
                      <SquareArrowOutUpRight className="h-[18px] w-[18px]" />
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const WebDataTable = () => {
    const headers = getFilteredHeaders(); // Get the active visible columns

    return (
      <>
        {isFetching || isGlobalLoading || loading || isLoading ? (
          <TableSkeleton />
        ) : currentThreads.length === 0 ? (
          <div className="w-full mt-4">
            {sourceThreads.length === 0 ? (
              renderEmptyState()
            ) : (
              <div className="f-col items-start gap-5 mt-0">
                <p className="no-records border border-gray-200 shadow-sm sahi-radius p-4 w-fit px-8 text-center">
                  <span>No records found for selected filters.</span>
                  <span
                    onClick={handleResetFilters}
                    className="text-sahi-primary-color underline hover:text-sahi-primary-color/80 cursor-pointer ml-2"
                  >
                    Clear filter.
                  </span>
                </p>
              </div>
            )}
          </div>
        ) : (
          <>
            <Card className="bg-sahi-white hidden lg:block !pt-0 overflow-hidden ">
              <CardContent className="px-0 !sahi-radius">
                <Table className="min-w-full table-auto">
                  <TableHeader>
                    <TableRow className="bg-gray-50/50 py-1.5 h-11 border-b">
                      {headers.map(({ key, label, className = "", style = {} }) => {
                        // Detect if column is an amount for alignment
                        const isAmount = key === "bizProspect" || (key === "status" && selectedFilters.status === "2");
                        return (
                          <TableHead
                            key={key}
                            className={cn(
                              "table-header-text cursor-pointer font-semibold text-black",
                              isAmount ? "text-right pr-6" : "text-left pl-4"
                            )}
                            style={style}
                            onClick={() => sortData(key)}
                          >
                            <div className={cn("flex items-center", isAmount ? "justify-end" : "justify-start", className)}>
                              {label}
                              {sortConfig.key === key && (
                                sortConfig.direction === "asc" ? <ArrowUp className="ml-1 h-4 w-4" /> : <ArrowDown className="ml-1 h-4 w-4" />
                              )}
                            </div>
                          </TableHead>
                        );
                      })}
                      {/* Empty head for the static Actions column */}
                      <TableHead className="w-40 text-right pr-4"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {currentThreads.map((thread) => (
                      <TableRow
                        key={thread.threadID}
                        className="hover:bg-gray-50 cursor-pointer h-10 align-middle "
                        onClick={() => handleRowClick(thread)}
                      >
                        {headers.map((header) => {
                          // Dynamic alignment check
                          const isAmountCol = header.key === "bizProspect" || (header.key === "status" && selectedFilters.status === "2");
                          const cellClass = isAmountCol ? "text-right pr-6" : "text-left pl-4";

                          // 1. TYPE ICON
                          if (header.key === "type") return (
                            <TableCell key={header.key} className="w-[3vw] pl-4 text-left">
                              <ThreadSahiIcon tID={thread.typeID} hoverName />
                            </TableCell>
                          );

                          // 2. CONTACT / BUSINESS NAME
                          if (header.key === "bizName") return (
                            <TableCell key={header.key} className={cn("max-w-[250px]", cellClass)}>
                              <span className="font-medium text-text-color truncate block">
                                {thread.connectionData?.bizName || thread.connectionData?.contactName || "-"}
                              </span>
                            </TableCell>
                          );

                          if (header.key === "status") return (
                            <TableCell key={header.key} className={cn("whitespace-nowrap", cellClass)}>
                              {(() => {
                                // Support Ticket specific mapping
                                if (Number(tID) === 3) {
                                  return thread.statusID === 0 ? "Open" : "Closed";
                                }

                                if (selectedFilters.status === "all") {
                                  if (thread.statusID === 0) return "Open";
                                  if (thread.statusID === 2) return "Closed";
                                  if (thread.failureReason) return "Rejected";
                                  if (thread.disqlReason) return "Disqualified";
                                  return "Closed";
                                }
                                // ... existing rejection/disqualification logic
                                return thread.statusID === 0 ? "Open" : "Closed";
                              })()}
                            </TableCell>
                          );

                          // 4. GENERAL AMOUNT (Offering Value)
                          if (header.key === "bizProspect") return (
                            <TableCell key={header.key} className={cellClass}>
                              <FormatAmount amount={thread.bizProspect} currency={currencySymbol} showCurr={false} showDecimalPoints={isDecimal ? 2 : 0} />
                            </TableCell>
                          );

                          // 5. REMAINING FIELDS (Dates, Reference, Executive, Next Action)
                          let val = "-";
                          let styleObj = {};
                          if (header.key === "mainProduct") val = thread.mainProduct;
                          else if (header.key === "sinceDT") val = thread.sinceDTF;
                          else if (header.key === "dueDT") val = <span className={thread.dueDTColor}>{thread.dueDTF}</span>;
                          else if (header.key === "reference") val = thread.reference;
                          else if (header.key === "pocUserName") val = thread.pocUserName;
                          else if (header.key === "nextTaskDT") {
                            val = thread.nextTaskDT ? `${thread.nextTaskDTF} ${thread.nextTaskTyp ? `- ${thread.nextTaskTyp}` : ""}` : "-";
                            styleObj = { color: thread.nextTaskDTColor };
                          }

                          return (
                            <TableCell key={header.key} className={cellClass} style={styleObj}>
                              {val || "-"}
                            </TableCell>
                          );
                        })}

                        {/* STATIC ACTIONS COLUMN */}
                        <TableCell className="text-right pr-4 w-40">
                          <div className="flex justify-end items-center gap-2">
                            {thread.thrFile && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setPreviewFilePath(thread.thrFile);
                                    }}
                                    className="h-6 w-6 p-0 flex items-center justify-center rounded-md hover:bg-sahi-blue/10 text-sahi-blue hover:shadow-sm transform-gpu transition-transform transition-colors duration-200 ease-out hover:scale-110 active:scale-95"
                                  >
                                    <Paperclip className="h-4 w-4" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>View Attached Document</p>
                                </TooltipContent>
                              </Tooltip>
                            )}
                            {Number(thread.typeID) === 2 && Number(thread.statusID) === 2 && Boolean(thread.connectionData?.mobile) && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleFdbkRequest(thread);
                                    }}
                                    className="h-6 w-6 p-0 flex items-center justify-center rounded-md hover:bg-sahi-teal/10 text-sahi-teal hover:shadow-sm transform-gpu transition-transform transition-colors duration-200 ease-out hover:scale-110 active:scale-95"
                                  >
                                    <MessageSquareText className="h-4 w-4" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Send feedback request on WhatsApp</p>
                                </TooltipContent>
                              </Tooltip>
                            )}
                            <ContactActions 
                              connectionData={thread?.connectionData} 
                              connID={thread?.connID} 
                              stageID={thread?.currStageID}
                            />
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Pagination Component  */}
            <PaginationComp
              currentPage={currentPage}
              totalPages={totalPages}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(size) => setRowsPerPage(size)}
              onPageChange={(page) => setCurrentPage(page)}
            />
          </>
        )}
      </>
    );
  };

  //threadtype filter
  const filteredFlowGroups = {
    customer: threadTypes.filter((t) => t.Flow === "In"),
    supplier: threadTypes.filter((t) => t.Flow === "Out"),
  };

  // Count how many flow groups have options (used in multiple places)
  const activeGroups = Object.values(filteredFlowGroups).filter(
    (g) => g?.length > 0
  ).length;

  const renderAddThreadButtonMobile = (isempty) => {
    if (!canCreateThread || currentThreads.length > 4) return null;

    const isOrder = Number(threadTypeInfo.typeID) === 2;
    const label = isOrder
      ? "Enter Sale"
      : `Add ${threadTypeInfo.typeNameSng || "Record"}`;

    return (
      <div className={`flex flex-col sm:flex-row gap-2 ${!isempty ? "pt-2 mt-1" : "mt-1"}`}>
        <Button
          onClick={() => handleThreadTypeSelect(threadTypeInfo.typeID)}
          className=""
        >
          <Plus className="h-4 w-4" />
          {label}
        </Button>
      </div>
    );
  };

  //Component for thread fileter type wise
  const ThreadTypeFilterDesktop = () => {
    const typesStr = selectedFilters.types?.toString();

    const toggleTypeFilter = (tID) => {
      const tIDStr = tID.toString();
      setSelectedFilters((prev) => {
        const updated = typesStr.includes(tIDStr)
          ? typesStr.replace(tIDStr, "")
          : typesStr + tIDStr;
        return { ...prev, types: updated, stage: "all" };
      });
      setTempFilters((prev) => {
        const updated = typesStr.includes(tIDStr)
          ? typesStr.replace(tIDStr, "")
          : typesStr + tIDStr;
        return { ...prev, types: updated, stage: "all" };
      });
    };

    return (
      <div className="flex flex-wrap gap-4">
        {["customer", "supplier"].map((groupKey) => {
          const label =
            groupKey === "customer" ? "Opportunities :" : "Supply Chain :";
          const options = filteredFlowGroups[groupKey];

          // Hide section if only one mode is active
          if (options.length <= 0 || activeGroups === 1) return null;

          return (
            <Button
              key={groupKey}
              variant="semidefaultcol"
              className={cn(
                "w-auto  bg-white text-black sahi-radius px-3 py-2",
                "filter-border",
                "flex flex-row items-center align-middle"
              )}
            >
              {/* Header */}
              <div className="flex items-center gap-2 ">
                <label className="f-row items-center gap-2 cursor-pointer">
                  <span className="font-medium text-sm">{label}</span>
                </label>
              </div>

              <div className="flex flex-wrap items-center gap-1">
                {options.map((t) => (
                  <label
                    key={t.TID}
                    className="flex items-center gap-1 cursor-pointer sahi-radius px-2 py-1"
                  >
                    <Checkbox
                      checked={typesStr.includes(t.TID.toString())}
                      onCheckedChange={() => toggleTypeFilter(t.TID)}
                      className={checkBoxCss}
                    />
                    <div className="ml-1 f-row gap-1 items-center align-middle">
                      <ThreadSahiIcon tID={t.TID} className="h-4 w-4 " />
                      <span className="text-sm font-normal text-black">
                        {t.Sn || ""}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
            </Button>
          );
        })}
      </div>
    );
  };

  const filterButton = useMemo(() => {
    let activeFilterClass =
      isFilterSelected && !isMobile ? "filter-active" : "";
    let filterIconToggleClass =
      "h-4 w-4 " + isMobile
        ? ""
        : isFilterSelected
          ? "text-sahi-primary-color"
          : "text-white group-hover:text-sahi-primary-color";
    return (
      <Button
        className={cn(filterBtn, activeFilterClass)}
        onClick={() => setIsFilterSelected(true)}
        aria-label="Toggle Filter"
      >
        <div className="h-6 w-6 flex items-center justify-center transition-all">
          <Filter className={cn(filterIconToggleClass)} />
        </div>
      </Button>
    );
  }, [isFilterSelected, isMobile]);


  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-sahi-primary-color mr-3" />
        <span className="text-text-color">{`Loading ${threadTypes[threadTypeInfo.typeID - 1]?.Typ ?? ""
          })}`}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Card className="border-sahi-red bg-red-50">
          <CardContent className="p-4">
            <p className="text-sahi-red font-medium">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }


  return (
    <TooltipProvider>
      <div
        className={`${embedded ? 'space-y-1' : 'space-y-3 lg:space-y-6'} overflow-x-hidden`}
      >
        {/*--------------- Header ------------*/}
        {/* Web Layout */}
        <div className="hidden lg:flex justify-between items-center gap-3">
          <div className="flex items-center flex-grow">
            <h1 className="text-3xl font-bold text-text-color mr-4 web-header-name">
              {isInqOnlyMode && Number(tID) === 2
                ? "Sales"
                : `${threadType?.TypSng || "Threads"} History`
              }
            </h1>
            {/* Search Box */}

            <div className="flex items-center justify-end flex-grow gap-3">
              {/* Show search only if there are more than 10 filtered records or currently searching */}

              {(filteredThreads.length !== 0 && Number(tID) === 2) && (<div
                className={cn(
                  "flex items-center px-3 lg:px-4 h-8 lg:h-10",
                  "w-fit bg-white text-text-color rounded-lg border filter-border shadow-sm"
                )}
              >
                <span className="f-row gap-1 whitespace-nowrap text-xs lg:text-sm">
                  <span>Value :</span>
                  <span className="font-medium">
                    <FormatAmount
                      amount={filteredThreads.reduce(
                        (sum, item) => sum + (Number(item.bizProspect) || 0),
                        0
                      )}
                      currency={currencySymbol}
                      showCurr={true}
                    />
                  </span>

                  <span className="mx-1 text-sub-text-color">|</span>

                  <span>Total :</span>
                  <span className="font-medium">
                    {filteredThreads.length}
                  </span>
                </span>
              </div>)}


              {(filteredThreads.length > 10 || searchTerm) && (
                <div className="relative w-120 filter-border">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-dropdown-white" />
                  <Input
                    placeholder="Search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 h-10 border-none focus:outline-none focus:ring-0 shadow-none"
                  />
                </div>
              )}
            </div>

          </div>
          <div className="flex items-center gap-3">
            {/* Import Button */}
            {/* <Integrations buttonText={["14", "41", "1", "4"].includes(selectedFilters.types) ? "Import" : "Import Inquiries"} threadInfo={threadTypeInfo} /> */}
            {/* Add Threads */}
            {/* {threadTypeInfo.typeID === 0 ? (
                            <ThreadDropdown
                                onThreadTypeSelect={handleThreadTypeSelect}
                                iconButton={false}
                            />
                        ) : (
                            <Button
                                onClick={() => handleThreadTypeSelect(threadTypeInfo.typeID)}
                                className="button-config text-base sahi-primary-button flex items-center py-[6px] px-[10px]"
                            >
                                <Plus className="h-4 w-4" />
                                Add {threadTypeInfo.typeNameSng || "thread"}
                            </Button>
                        )} */}

            {/* {!isThreadsEmpty() && ( */}
            <>
              {canCreateThread && (
                <ThreadDropdown
                  onThreadTypeSelect={handleThreadTypeSelect}
                  iconButton={false}
                  threadTypeIDs={[tID]}
                />
              )}
              <Button
                className={cn(
                  "button-config sahi-primary-button flex items-center justify-center group px-2.5 py-[5px] ",
                  isFilterSelected && "filter-active"
                )}
                onClick={() => setIsFilterSelected((prev) => !prev)}
                aria-label="Toggle filter"
              >
                <div className="h-6 w-6 flex items-center justify-center transition-all">
                  <Filter
                    className={cn(
                      "h-4 w-4",
                      isFilterSelected
                        ? "text-sahi-primary-color"
                        : "text-white group-hover:text-sahi-primary-color"
                    )}
                  />
                </div>
              </Button>
            </>
            {/* )} */}
          </div>
        </div>
        {/* Mobile view */}
        {!embedded && (
          <ConditionalDiv className="f-col lg:hidden gap-3 ">
            {/* Search (Show only if filtered records > 10 or searching) */}
            {(filteredThreads.length > 10 || searchTerm) && (
              <div className="w-full flex items-center gap-2 ">
                <div className="relative flex-grow">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-dropdown-white" />
                  <Input
                    placeholder={`Search`}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 h-10 w-full"
                  />
                </div>
              </div>
            )}
          </ConditionalDiv>
        )}

        {/*--------------- Thread type Filter (Type/Status) ------------*/}
        {!isMobile && (
          <div className="f-row flex-wrap gap-4">
            {/* <ThreadTypeFilterDesktop /> */}
          </div>
        )}

        {/*--------------- Filter ------------*/}
        {isFilterSelected &&
          (isMobile ? (
            <Drawer
              open={isDrawerOpen}
              onOpenChange={() => {
                setIsDrawerOpen();
                setIsFilterSelected(false);
              }}
            >
              <DrawerContent className="p-0 [&>div:first-child]:hidden">
                <div className="f-col h-[100dvh] !rounded-t-[10px]">
                  {/* Header */}
                  <div className="flex plain items-center justify-between p-4 border-b-2 border-gray-200 text-xl !rounded-t-[10px]">
                    <p className="text-lg font-semibold text-sahi-primary-color">
                      Filters & Sorting
                    </p>
                    <button
                      className="group flex items-center gap-1 text-sm font-light  hover:underline transition duration-200  text-sahi-primary-color"
                      onClick={() => {
                        handleResetFilters();
                        setIsFilterSelected(false);
                        setIsDrawerOpen(false);
                      }}
                    >
                      <RiResetLeftFill className="group-hover:rotate-[-20deg] transition-transform duration-200" />
                      <span>Reset</span>
                    </button>
                  </div>
                  {/* Main Content */}
                  <div className="flex flex-1 overflow-y-auto">
                    {/* Scrollable Filters */}
                    <div className="flex-1 p-3 overflow-y-auto filter-scroll-container">
                      {[
                        ...filterOptions.slice(0),
                        {
                          key: "sort",
                          label: "Sort by",
                          options: [
                            "Received (newest first)",
                            "Received (oldest first)",
                            "Amount (highest first)",
                          ],
                          type: "sort",
                        },
                      ].flatMap(
                        (filterItem) => {
                          const {
                            key,
                            label,
                            options,
                            displayValue,
                            type,
                            mobiledisabled,
                            mobileDropdownText,
                            MobileLabel,
                            showAllOption,
                            showAllOptionNum,
                            mobileNoLabel,
                            isSorting,
                            isHidden,
                          } = filterItem;

                          if (isHidden) return [];
                          if (key === "types" && (!options || options.length <= 1)) return [];

                          // 1. Skip these here, they will be rendered inside the 'dateRange' block
                          if (['otherMonth', 'otherQuarter', 'otherYear'].includes(key)) return [];

                          // 2. Specialized logic for Period + Sub-Period
                          if (key === "dateRange") {
                            // Find the correct sub-filter object from filterOptions
                            const subFilterKey = tempFilters.dateRange === "Other Month" ? "otherMonth" :
                              tempFilters.dateRange === "Other Quarter" ? "otherQuarter" :
                                tempFilters.dateRange === "Other Year" ? "otherYear" : null;

                            const subFilter = subFilterKey ? filterOptions.find(f => f.key === subFilterKey) : null;

                            return [
                              <div key={key} id={key} className="mb-2">
                                <h3 className="font-semibold text-md text-text-color mb-1 rounded-t-[10px] ">{MobileLabel || label}</h3>
                                <div className="flex gap-2 items-center">
                                  <div className="flex-1">
                                    <DrawerFilter
                                      {...filterItem}
                                      mobileNoLabel={true} // Hide label for horizontal layout
                                      value={tempFilters[key]}
                                      onChange={(val) => setTempFilters(prev => ({ ...prev, [key]: val }))}
                                    />
                                  </div>
                                  {subFilter && (
                                    <div className="flex-1">
                                      <DrawerFilter
                                        {...subFilter}
                                        mobileNoLabel={true}
                                        value={tempFilters[subFilter.key]}
                                        onChange={(val) => setTempFilters(prev => ({ ...prev, [subFilter.key]: val }))}
                                      />
                                    </div>
                                  )}
                                </div>
                              </div>,
                              <div key="select-date-mobile" className="mb-4">
                                <label className="flex items-center cursor-pointer select-none gap-2">
                                  <input
                                    type="checkbox"
                                    id="selectDateFilterCheckboxMobile"
                                    checked={tempFilters.selectDate}
                                    onChange={(e) => {
                                      setTempFilters((prev) => ({
                                        ...prev,
                                        selectDate: e.target.checked,
                                        selectedDate: e.target.checked ? prev.selectedDate : null,
                                        dateRange: "all"
                                      }));
                                    }}
                                    className="h-4 w-4 rounded border-gray-300 filter-border text-sahi-primary-color focus:ring-sahi-primary-color"
                                  />
                                  <span className="text-sm font-medium text-text-color">Select Date</span>
                                </label>

                                {tempFilters.selectDate && (
                                  <DatePicker
                                    value={tempFilters.selectedDate || ""}
                                    onChange={(date) => {
                                      let dateStr = null;
                                      if (date) {
                                        const dateObj = new Date(date);
                                        const year = dateObj.getFullYear();
                                        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
                                        const day = String(dateObj.getDate()).padStart(2, '0');
                                        dateStr = `${year}-${month}-${day}`;
                                      }
                                      setTempFilters((prev) => ({ ...prev, selectedDate: dateStr, selectDate: !!dateStr, }));
                                    }}
                                    placeholder="Select date"
                                    disableFuture={false}
                                    className="!space-y-0 mt-3 w-1/2"
                                  />
                                )}
                              </div>
                            ]
                          }

                          const currentFilter = (
                            <div key={key} id={key} className="mb-4">
                              <DrawerFilter
                                key={key}
                                id={key}
                                label={label}
                                options={options}
                                value={tempFilters[key]}
                                displayValue={displayValue}
                                onChange={(value) => {
                                  setTempFilters((prev) => ({
                                    ...prev,
                                    [key]: value,
                                  }));
                                }}
                                disabled={mobiledisabled}
                                sortConfig={sortConfig}
                                setTempsortConfig={setTempsortConfig}
                                tempsortConfig={tempsortConfig}
                                sortOptionMap={sortKeyMapMobileFilter}
                                type={type}
                                mobileDropdownText={mobileDropdownText}
                                MobileLabel={MobileLabel}
                                showAllOption={showAllOption}
                                showAllOptionNum={showAllOptionNum}
                                mobileNoLabel={mobileNoLabel}
                                isSorting={isSorting}
                              />
                            </div>
                          );

                          // Insert "Select Date" after "Other Year" filter (completes period section)
                          if (key === "dateRange") {
                            return [
                              currentFilter,
                              <div key="select-date-mobile" className="mb-4">
                                <label className="flex items-center cursor-pointer select-none gap-2">
                                  <input
                                    type="checkbox"
                                    id="selectDateFilterCheckboxMobile"
                                    checked={tempFilters.selectDate}
                                    onChange={(e) => {
                                      setTempFilters((prev) => ({
                                        ...prev,
                                        selectDate: e.target.checked,
                                        selectedDate: e.target.checked ? prev.selectedDate : null,
                                        dateRange: "all"
                                      }));
                                    }}
                                    className="h-4 w-4 rounded border-gray-300 filter-border text-sahi-primary-color focus:ring-sahi-primary-color"
                                  />
                                  <span className="text-sm font-medium text-text-color">Select Date</span>
                                </label>

                                {tempFilters.selectDate && (
                                  <DatePicker
                                    value={tempFilters.selectedDate || ""}
                                    onChange={(date) => {
                                      let dateStr = null;
                                      if (date) {
                                        const dateObj = new Date(date);
                                        const year = dateObj.getFullYear();
                                        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
                                        const day = String(dateObj.getDate()).padStart(2, '0');
                                        dateStr = `${year}-${month}-${day}`;
                                      }
                                      setTempFilters((prev) => ({ ...prev, selectedDate: dateStr, selectDate: !!dateStr, }));
                                    }}
                                    placeholder="Select date"
                                    disableFuture={false}
                                    className="!space-y-0 mt-3 w-1/2"
                                  />
                                )}
                              </div>
                            ];
                          }

                          return [currentFilter];
                        }
                      )}
                    </div>
                  </div>
                  {/* Always Visible Footer */}
                  <DrawerFooter className="sticky bottom-0 border-t-2 p-3 bg-sahi-white">
                    <DrawerClose>
                      <div className="f-row justify-start gap-2">
                        <Button
                          className="sahi-primary-button"
                          onClick={() => {
                            setSelectedFilters(tempFilters);
                            setIsDrawerOpen(false);
                            setIsFilterSelected(false);
                            sortData(
                              tempsortConfig.key,
                              tempsortConfig.direction
                            );
                          }}
                        >
                          Apply
                        </Button>
                        <Button
                          variant="outline"
                          className="sahi-secondary-button"
                          onClick={handleCancel}
                        >
                          Cancel
                        </Button>
                      </div>
                    </DrawerClose>
                  </DrawerFooter>
                </div>
              </DrawerContent>
            </Drawer>
          ) : (
            <div className="hidden lg:flex flex-wrap gap-4 !mt-0">
              {/* Period Filter Component */}
              <PeriodFilter
                value={selectedFilters}
                onChange={(newFilters) => {
                  setSelectedFilters(newFilters);
                  setTempFilters(newFilters);
                }}
                monthlyData={monthlyJson}
                showMonthFilter={true}
                showQuarterFilter={true}
                showYearFilter={true}
              />



              {/* Other Filters (Status, Contact, Executive) */}
              {filterOptions.slice(1).filter(f =>
                !['dateRange', 'otherMonth', 'otherQuarter', 'otherYear'].includes(f.key) && !f.isHidden
              ).map(
                ({
                  key,
                  label,
                  options,
                  displayValue,
                  searchEnabled,
                  disabled,
                  mobileLabel
                }) => (
                  <CommonFilter
                    key={key}
                    label={label}
                    options={options}
                    value={selectedFilters[key]}
                    onChange={(value) => {
                      setSelectedFilters((prev) => ({ ...prev, [key]: value }));
                      setTempFilters((prev) => ({ ...prev, [key]: value }));
                    }}
                    displayValue={displayValue}
                    disabled={disabled}
                    searchEnabled={searchEnabled}
                    showClearButton={true}
                    mobileLabel={mobileLabel}
                  />
                )
              )}

              {/* Select Date Filter */}
              <label className="flex items-center cursor-pointer select-none">
                <input
                  type="checkbox"
                  id="selectDateFilterCheckbox"
                  checked={selectedFilters.selectDate}
                  onChange={(e) => {
                    setSelectedFilters((prev) => ({ ...prev, selectDate: e.target.checked, selectedDate: e.target.checked ? prev.selectedDate : null }));
                    setTempFilters((prev) => ({ ...prev, selectDate: e.target.checked, selectedDate: e.target.checked ? prev.selectedDate : null }));
                  }}
                  className="mr-2 h-4 w-4 rounded border-gray-300 filter-border text-sahi-dark-blue focus:ring-sahi-dark-blue"
                />
                <span className="text-sm font-medium text-text-color whitespace-nowrap">Select Date</span>
              </label>

              {selectedFilters.selectDate && (
                <div className="flex items-center gap-2">
                  <DatePicker
                    value={selectedFilters.selectedDate || ""}
                    onChange={(date) => {
                      let dateStr = null;
                      if (date) {
                        const dateObj = new Date(date);
                        const year = dateObj.getFullYear();
                        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
                        const day = String(dateObj.getDate()).padStart(2, '0');
                        dateStr = `${year}-${month}-${day}`;
                      }
                      setSelectedFilters((prev) => ({ ...prev, selectedDate: dateStr, selectDate: !!dateStr, }));
                      setTempFilters((prev) => ({ ...prev, selectedDate: dateStr, selectDate: !!dateStr, }));
                    }}
                    placeholder="Select date"
                    disableFuture={false}
                    className="!space-y-0 !h-10 w-40"
                    useAsFilter={true}
                  />
                </div>
              )}

            </div>
          ))}

        {/*--------------- Data Section ------------*/}
        {isMobile ? (
          <div className="block lg:hidden">
            <div
              ref={parentRef}
              className="overflow-auto scrollbar-hide threads-virtualized-scrollbar"
            >
              {isFetching || isGlobalLoading || loading || isLoading ? (
                <ListSkeleton />
              ) : currentThreads.length > 0 ? (
                <div
                  className="scrollbar-hide overflow-y-hidden "
                  style={{
                    position: "relative",
                  }}
                >
                  {(rowVirtualizer.getVirtualItems().length > 0 ? rowVirtualizer.getVirtualItems() : currentThreads.map((_, i) => ({ index: i, measureElement: null }))).map((virtualRow) => {
                    const thread = currentThreads[virtualRow.index];
                    if (!thread) return null;
                    return (
                      <div
                        key={thread.threadID}
                        ref={virtualRow.measureElement}
                        className="  w-full"
                      >

                        <MobileCard
                          thread={thread}
                          threadTypeInfo={threadTypeInfo}
                          threadTypes={threadTypes}
                        />
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="w-full ">
                  {sourceThreads.length === 0 ? (
                    // CASE 1: True Empty State (No records exist for the selected period)
                    renderEmptyState()
                  ) : (
                    // CASE 2: Filtered Empty State (Data exists but hidden by filters/search)
                    <div className="f-col items-start gap-5 ">
                      <p className="no-records border border-gray-200 shadow-sm sahi-radius p-4 w-full text-center">
                        <span>No data found for selected filters.</span>
                        <span
                          onClick={handleResetFilters}
                          className="text-sahi-primary-color underline hover:text-sahi-primary-color/80 cursor-pointer ml-2"
                        >
                          Clear filter.
                        </span>
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
            {/* Add Button as Card */}
            {renderAddThreadButtonMobile(currentThreads.length > 0 ? true : false)}
          </div>
        ) : (
          <WebDataTable />
        )}
        <ThreadEntry
          triggerButton={false}
          threadTypeID={selectedThreadTypeFromDropdown || (tID && Number(tID) !== 0 ? Number(tID) : 1)}
          threadID={selectedThread?.threadID}
          preselectedConnID={selectedThread?.connID}
          open={threadEntryOpen}
          onOpenChange={(open) => {
            setThreadEntryOpen(open);
            if (!open) {
              // Don't reset immediately - let the component unmount first
              setTimeout(() => {
                setSelectedThreadTypeFromDropdown(tID && Number(tID) !== 0 ? Number(tID) : null);
                setSelectedThread(null);
              }, 100);
            }
          }}
          onThreadUpdated={() => {
            // Trigger refresh which will clear cache and fetch fresh data
            setRefreshTrigger(prev => prev + 1);
          }}
          calledFrom={
            historyMode
              ? ""
              : selectedThreadTypeFromDropdown == 2 && isInqOnlyMode
                ? "ORDDONE"
                : (isAgencyAuthorized && selectedThreadTypeFromDropdown == 3) ? "" : (selectedThreadTypeFromDropdown == 1 || tID == 1 ? "" : "THRREP")
          }
          prefilledData={
            selectedThread
              ? {
                connID: selectedThread.connID,
                contactPerson: selectedThread.connectionData?.contactName,
                product: selectedThread.mainProduct,
                model: selectedThread.model,
                reference: selectedThread.reference,
                quantity: selectedThread.mainQty,
                businessValue: selectedThread.bizProspect,
                notes: selectedThread.notes,
                source: selectedThread.source,
                thrFile: selectedThread.thrFile,
                inquiryThreadID: selectedThread.threadID,
                ...(selectedThread.sinceDT && {
                  sinceDT: selectedThread.sinceDT,
                }),
              }
              : null
          }
          extOperation={historyMode ? "EDIT" : ""}
          isDisabled={isInqOnlyMode && selectedThread?.typeID === 1}
        />
        {/* Thread details  */}
        {selectedThread && (
          <ThrDetails
            isOpen={isThreadDetialsOpen}
            onClose={() => {
              setIsThreadDetailsOpen(false);
            }}
            threadID={selectedThread?.threadID}
            isHistoryview={true}
            onThreadUpdated={() => {
              setRefreshTrigger(prev => prev + 1);
            }}
            initialData={{
              ThreadID: selectedThread?.threadID || selectedThread?.ThreadID,
              TypeID: selectedThread?.typeID || selectedThread?.TypeID,
              AgencyID: selectedThread?.agencyID || selectedThread?.AgencyID,
              ConnID: selectedThread?.connID || selectedThread?.ConnID,
              MainProduct: selectedThread?.mainProduct || selectedThread?.MainProduct,
              MainQty: selectedThread?.mainQty || selectedThread?.MainQty,
              Model: selectedThread?.model || selectedThread?.Model,
              POCUserID: selectedThread?.pocUserID || selectedThread?.POCUserID,
              StatusID: selectedThread?.statusID !== undefined ? selectedThread?.statusID : selectedThread?.StatusID,
              ThrFile: selectedThread?.thrFile || selectedThread?.ThrFile || selectedThread?.thrfile,
              DueDT:
                (selectedThread?.dueDT == "-" || selectedThread?.DueDT == "-") ? null : (selectedThread?.dueDT || selectedThread?.DueDT),
              SinceDT: selectedThread?.sinceDT || selectedThread?.SinceDT,
              Reference: selectedThread?.reference || selectedThread?.Reference,
              BizProspect: selectedThread?.bizProspect !== undefined ? selectedThread?.bizProspect : selectedThread?.BizProspect,
              CurrStageID: selectedThread?.currStageID || selectedThread?.CurrStageID,
              Source: selectedThread?.source || selectedThread?.Source,
              Notes: selectedThread?.notes || selectedThread?.Notes,
              FdbkNotes: selectedThread?.feedbackNotes || selectedThread?.FdbkNotes,
              FdbkScore: selectedThread?.feedbackScore || selectedThread?.FdbkScore,
              FailureReason: selectedThread?.failureReason || selectedThread?.FailureReason,
              DisqlReason: selectedThread?.disqlReason || selectedThread?.DisqlReason,
              CustomFields: selectedThread?.customFields || selectedThread?.CustomFields || selectedThread?.ExtraFields,
              connectionData: {
                BizName: selectedThread?.connectionData?.bizName || selectedThread?.connectionData?.BizName || selectedThread?.bizName,
                ContactName: selectedThread?.connectionData?.contactName || selectedThread?.connectionData?.ContactName || selectedThread?.contactName,
                CC: selectedThread?.connectionData?.cc || selectedThread?.connectionData?.CC,
                Mobile: selectedThread?.connectionData?.mobile || selectedThread?.connectionData?.Mobile,
                Email: selectedThread?.connectionData?.email || selectedThread?.connectionData?.Email,
                ConnType: selectedThread?.connectionData?.connType || selectedThread?.connectionData?.ConnType,
                Notes: selectedThread?.connectionData?.Notes || selectedThread?.connectionData?.notes
              },
            }}
            isDisabled={isInqOnlyMode && selectedThread?.typeID === 1}
          />
        )}

        <ConnectionDetails
          connID={selectedConnID}
          isOpen={isConnDetailOpen}
          onClose={() => setIsConnDetailOpen(false)}
          expandThreadID={selectedThread?.threadID}
        />

        <ConnectionDetailsMobile
          connID={selectedConnID}
          isOpen={isMobileConnDetailOpen}
          onClose={() => setIsMobileConnDetailOpen(false)}
          expandThreadID={selectedThread?.threadID}
        />

        <AttachmentPreviewModal
          open={Boolean(previewFilePath)}
          onOpenChange={(open) => !open && setPreviewFilePath(null)}
          filePath={previewFilePath}
        />
      </div>
    </TooltipProvider>
  );
};

export default Threads;
